import React, { useCallback, useEffect, useState, Suspense, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { jsPDF } from 'jspdf';
import {
  PlusIcon,
  ClipboardDocumentListIcon,
  FunnelIcon,
  PuzzlePieceIcon,
  TrashIcon,
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  DocumentTextIcon,
  DocumentArrowUpIcon,
  ClipboardIcon,
  VideoCameraIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  EllipsisVerticalIcon,
  BoltIcon,
  PhoneIcon,
  PencilSquareIcon,
  ChevronDownIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import AssessmentCreateFlowModal from '../assessments/AssessmentCreateFlowModal';
import AutomationsModal from '../../components/screenings/AutomationsModal';
import ConnectedScreeningsModal from '../../components/screenings/ConnectedScreeningsModal';
import GoColorsPayrollDownloadMenu from '../../components/screenings/GoColorsPayrollDownloadMenu';
import AIInterviewLibraryPickerModal from '../ai/AIInterviewLibraryPickerModal';
import { JOBS_QUERY_KEY } from '../../hooks/useJobs';
import useBodyScrollLock from 'src/hooks/useBodyScrollLock';
import { useAuth } from '../../contexts/AuthContext';
import ResumeScoreConfig from '../../components/screenings/ResumeScoreConfig';
import DocumentCollectionConfig from '../../components/screenings/DocumentCollectionConfig';
import WhatsAppAgentModal from '../../components/screenings/WhatsAppAgentModal';
import QualifyingAgentModal from '../../components/screenings/QualifyingAgentModal';
import { deleteHistory } from '../sourcing/coresignalApi';
import { buildInterviewFromLibraryTemplate } from '../../lib/interviewTypes';
const AIInterviewCreate = React.lazy(() => import('../ai/AIInterviewCreate'));
const SourcingAgent = React.lazy(() => import('../sourcing/SourcingAgent'));

const formatLocationDisplay = (location, locationType) => {
  const rawLocationValue = Array.isArray(location) ? location.join(', ') : location || '—';
  const rawLocationType = locationType || '';

  const locationValue = String(rawLocationValue).trim();
  const typeValue = String(rawLocationType).trim();

  let display = `${locationValue}${typeValue ? ` (${typeValue})` : ''}`.trim();

  const normalizedDisplay = display.replace(/\s+/g, ' ').toLowerCase();
  const normalizedLocation = locationValue.replace(/\s+/g, ' ').toLowerCase();
  const normalizedType = typeValue.replace(/\s+/g, ' ').toLowerCase();

  // If there is effectively no city/location but the work arrangement is remote,
  // normalize all variants like "— (remote)", "- (remote)", "remote (remote)" etc. to "remote".
  if (
    (normalizedLocation === '' || normalizedLocation === '—' || normalizedLocation === '-') &&
    normalizedType === 'remote'
  ) {
    return 'remote';
  }

  if (
    normalizedDisplay === '— (remote) (remote)' ||
    normalizedDisplay === '- (remote) (remote)' ||
    normalizedDisplay === '— (remote)' ||
    normalizedDisplay === '- (remote)' ||
    normalizedDisplay === 'remote (remote)'
  ) {
    return 'remote';
  }

  return display;
};

// CSS to override modal max-height when embedded
const embeddedModalStyles = `
  .embedded-modal-container {
    width: 100% !important;
    max-width: 100% !important;
  }
  .embedded-modal-container > div:first-child {
    max-height: 100% !important;
    height: 100% !important;
    min-height: 0 !important;
    width: 100% !important;
  }
  .embedded-modal-container .flex.h-full {
    max-height: 100% !important;
    height: 100% !important;
    width: 100% !important;
  }
  .embedded-modal-container .flex.flex-col.flex-1 {
    overflow-y: auto !important;
  }
  /* Ensure Assessment modal uses full width when embedded */
  .embedded-modal-container > div > div:first-child {
    width: 100% !important;
    max-width: 100% !important;
  }
  /* Ensure Interview modal structure gets proper height - simplified selector like Assessment */
  .interview-modal-wrapper > div:first-child {
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
  }
  /* Ensure all wrapper divs in the chain have height */
  .interview-modal-wrapper > div {
    height: 100% !important;
    min-height: 0 !important;
  }
  /* Ensure the root Interview modal div gets proper height */
  .interview-modal-wrapper .flex.h-full.w-full {
    height: 100% !important;
    max-height: 100% !important;
    min-height: 0 !important;
  }
  /* Target the main content area that contains the scrollable div - ensure it has height */
  .interview-modal-wrapper .flex.flex-col.min-w-0.bg-white {
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    height: 100% !important;
    min-height: 0 !important;
    flex: 1 !important;
  }
  /* Target the scrollable content area - matches actual DOM structure */
  .interview-modal-wrapper .flex.flex-col.min-w-0.bg-white .overflow-y-auto {
    overscroll-behavior-y: contain !important;
    overscroll-behavior: contain !important;
    -webkit-overflow-scrolling: touch !important;
    overflow-y: auto !important;
    flex: 1 !important;
    min-height: 0 !important;
  }
  /* Fallback selector - direct overflow-y-auto targeting */
  .interview-modal-wrapper .overflow-y-auto {
    overflow-y: auto !important;
    flex: 1 !important;
    min-height: 0 !important;
  }
  /* Remove max-width constraints from Interview modal content when embedded */
  .interview-modal-wrapper .overflow-y-auto > div {
    max-width: 100% !important;
    width: 100% !important;
  }
  /* Specifically target Step 1 and Step 2 content containers - remove max-width */
  .interview-modal-wrapper .space-y-4.max-w-2xl,
  .interview-modal-wrapper .space-y-6.max-w-4xl,
  .interview-modal-wrapper .max-w-2xl,
  .interview-modal-wrapper .max-w-4xl {
    max-width: 100% !important;
    width: 100% !important;
  }
  /* Ensure all direct children of scrollable area use full width */
  .interview-modal-wrapper .overflow-y-auto > * {
    max-width: 100% !important;
  }
  /* Remove max-width from main content containers (but preserve small text max-widths) */
  .interview-modal-wrapper .overflow-y-auto .space-y-4,
  .interview-modal-wrapper .overflow-y-auto .space-y-6 {
    max-width: 100% !important;
    width: 100% !important;
  }
`;

const JobScreeningsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSamtaGroup = String(user?.orgId) === '6a3b9faeb52a49d66cc5f63e';
  const isGoColors = user?.orgSlug === 'go-colors---go-fashion-india-ltd-sdf61l';
  const isSourcingEnabled = user?.features?.sourcing === true;
  const isWhatsAppAgentEnabled = user?.features?.whatsapp_agent === true;
  const isQualifyingAgentEnabled = user?.features?.qualifying_agent === true;
  const isApplicationFormEnabled = user?.features?.application_form === true;
  const armSalaryDetailsEnabled =
    user?.features?.arm_salary_details === true ||
    user?.features?.store_decision_emails === true ||
    user?.features?.store_list === true;

  const [job, setJob] = useState(location.state?.job || null);
  const [jobLoading, setJobLoading] = useState(true);
  const [jobError, setJobError] = useState('');
  const [showJobDescriptionModal, setShowJobDescriptionModal] = useState(false);

  const [screenings, setScreenings] = useState([]);
  const [screeningsLoading, setScreeningsLoading] = useState(true);
  const [screeningsError, setScreeningsError] = useState('');
  // Counts now come directly on each screening from backend summary API

  const fetchScreenings = useCallback(async () => {
    if (!jobId) return;
    try {
      setScreeningsLoading(true);
      const res = await axios.get(`/api/screenings/job/${jobId}/summary`);
      const screeningsData = res.data.screenings || [];
      setScreenings(screeningsData);
      setScreeningsError('');
    } catch (e) {
      setScreeningsError(e.response?.data?.message || 'Failed to load screenings');
      setScreenings([]);
    } finally {
      setScreeningsLoading(false);
    }
  }, [jobId]);


  const hasFetchedFullJob = useRef(false);

  useEffect(() => {
    let isMounted = true;
    const fetchJob = async () => {
      if (!jobId) {
        setJobLoading(false);
        return;
      }

      // If we already have the job with a description, or we've already fetched from the API, don't fetch again.
      // (The list API excludes 'description' for performance, so location.state.job usually lacks it)
      if ((job && 'description' in job) || hasFetchedFullJob.current) {
        setJobLoading(false);
        return;
      }

      try {
        setJobLoading(true);
        hasFetchedFullJob.current = true;
        const res = await axios.get(`/api/job-posts/jobs/${jobId}`);
        if (isMounted) {
          setJob(res.data.job);
        }
      } catch (e) {
        if (isMounted) {
          if (e.response?.status === 404) {
            navigate('/dashboard/job-posts', { replace: true });
            return;
          }
          setJobError(e.response?.data?.message || 'Failed to load job');
        }
      } finally {
        if (isMounted) {
          setJobLoading(false);
        }
      }
    };
    if (jobId) {
      fetchJob();
    }
    return () => {
      isMounted = false;
    };
  }, [jobId, job, navigate]);

  useEffect(() => {
    if (jobId) {
      fetchScreenings();
    }
  }, [jobId, fetchScreenings]);

  // Update job's lastAccessedAt when page is opened
  useEffect(() => {
    if (!jobId) return;
    axios
      .patch(`/api/job-posts/jobs/${jobId}`, {
        lastAccessedAt: new Date().toISOString(),
      })
      .catch(() => { });
  }, [jobId]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [includeResumeScoring, setIncludeResumeScoring] = useState(false);
  const [resumeScoringCriteria, setResumeScoringCriteria] = useState([]);
  const [resumeCriteriaCreating, setResumeCriteriaCreating] = useState(false);
  const [resumeCriteriaCreateError, setResumeCriteriaCreateError] = useState('');
  const [includeAssessment, setIncludeAssessment] = useState(false);
  const [includeInterview, setIncludeInterview] = useState(false);
  const [includeDocumentCollection, setIncludeDocumentCollection] = useState(false);
  const [requestedDocuments, setRequestedDocuments] = useState([]);
  const [enableDocumentVerification, setEnableDocumentVerification] = useState(false);
  const [includeApplicationForm, setIncludeApplicationForm] = useState(false);
  const defaultJourneyAutomation = user?.screeningRules?.journeyAutomation || {};
  const [journeyAutomationEnabled, setJourneyAutomationEnabled] = useState(defaultJourneyAutomation.enabled || false);
  const [journeyAutomationResumeMinScore, setJourneyAutomationResumeMinScore] = useState(defaultJourneyAutomation.resumeMinScore ?? 60);
  const [journeyAutomationAssessmentMinScore, setJourneyAutomationAssessmentMinScore] = useState(defaultJourneyAutomation.assessmentMinScore ?? 60);
  const [enableArmSalaryRequest, setEnableArmSalaryRequest] = useState(false);
  const [methodRemoveConfirm, setMethodRemoveConfirm] = useState(null); // { key: 'resume'|'assessment'|'interview'|'documentCollection', title, message, onConfirm }

  // Computed step offset: when resume scoring or document collection is selected,
  // we adjust the step numbering.
  const rsOff = includeResumeScoring ? 1 : 0;
  const dcOff = includeDocumentCollection ? 1 : 0;
  const reviewStep = 3 + rsOff + dcOff + (includeAssessment ? 1 : 0) + (includeInterview ? 1 : 0);

  const [deletingId, setDeletingId] = useState(null);
  const [screeningPendingDelete, setScreeningPendingDelete] = useState(null);
  const [showAutomations, setShowAutomations] = useState(false);
  const [showConnectedScreenings, setShowConnectedScreenings] = useState(false);
  const [showWhatsAppAgent, setShowWhatsAppAgent] = useState(false);
  const [showQualifyingAgent, setShowQualifyingAgent] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  // The screening the Qualifying Agent modal is editing, or null when it is creating a new one.
  const [qualifyingAgentEditing, setQualifyingAgentEditing] = useState(null);
  const [openActionsDropdown, setOpenActionsDropdown] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [activePageTab, setActivePageTab] = useState(() => {
    const path = location.pathname;
    if (path.includes('/sourcing')) return 'sourcing';
    return 'screening';
  });

  const actionButtonRefs = useRef({});

  // Sourcing sessions state
  const [sourcingSessions, setSourcingSessions] = useState([]);
  const [sourcingLoading, setSourcingLoading] = useState(false);
  const [sourcingError, setSourcingError] = useState('');
  const [showSourcingModal, setShowSourcingModal] = useState(false);
  const [deletingSourcingId, setDeletingSourcingId] = useState(null);
  const [sourcingSearch, setSourcingSearch] = useState('');
  const [selectedSourcingSession, setSelectedSourcingSession] = useState(null); // null = create mode, object = detail mode

  const activeSourcingSessions = sourcingSessions.filter(s => (s.totalFound || 0) > 0);

  const fetchSourcingSessions = useCallback(async () => {
    if (!jobId) return;
    setSourcingLoading(true);
    setSourcingError('');
    try {
      console.log('[fetchSourcingSessions] fetching for jobId:', jobId);
      const res = await axios.get(`/api/coresignal/history?jobId=${jobId}`);
      console.log('[fetchSourcingSessions] got', res.data?.length, 'sessions');
      setSourcingSessions(res.data || []);
    } catch (e) {
      console.error('[fetchSourcingSessions] error:', e?.response?.data || e.message);
      setSourcingError('Failed to load sourcing sessions');
    } finally {
      setSourcingLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (activePageTab === 'sourcing') {
      fetchSourcingSessions();
    }
  }, [activePageTab, fetchSourcingSessions]);

  const handleDeleteSourcingSession = async (id) => {
    setDeletingSourcingId(id);
    try {
      await deleteHistory(id);
      setSourcingSessions(prev => prev.filter(s => s._id !== id));
      toast.success('Session deleted');
    } catch (e) {
      toast.error('Failed to delete session');
    } finally {
      setDeletingSourcingId(null);
    }
  };

  // Step 3: Assessment Source selection (when includeAssessment is true)
  const [assessmentSource, setAssessmentSource] = useState(null); // 'ai' | 'library' | null
  // Step 4: Interview Source selection (when includeInterview is true and Assessment is already configured)
  const [interviewSource, setInterviewSource] = useState(null); // 'ai' | 'library' | null (library coming soon)

  // Step 4: Configure Components state
  const [configuredAssessmentId, setConfiguredAssessmentId] = useState(null);
  const [configuredAssessmentDuration, setConfiguredAssessmentDuration] = useState(null); // minutes, from assessment modal
  const [configuredInterviewId, setConfiguredInterviewId] = useState(null);
  const [assessmentConfigured, setAssessmentConfigured] = useState(false);
  const [interviewConfigured, setInterviewConfigured] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showInterviewLibraryPicker, setShowInterviewLibraryPicker] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  useBodyScrollLock(
    Boolean(screeningPendingDelete) ||
    showJobDescriptionModal ||
    isCreateOpen ||
    showDiscardConfirm,
  );

  // Fix Interview modal scrolling when embedded (step 4 when both, or step 3 when only interview)
  useEffect(() => {
    const interviewModalVisible = showInterviewModal && (createStep === 4 + rsOff || (createStep === 3 + rsOff && includeInterview && !includeAssessment));
    if (!interviewModalVisible) return;

    let handleWheel = null;
    let scrollableArea = null;

    // Small delay to ensure DOM is ready after Suspense loads
    const timer = setTimeout(() => {
      // Try multiple selectors to find the scrollable area
      scrollableArea =
        document.querySelector(
          '.interview-modal-wrapper .flex.flex-col.min-w-0.bg-white .overflow-y-auto'
        ) || document.querySelector('.interview-modal-wrapper .overflow-y-auto');

      if (scrollableArea) {
        // Find the parent container that should constrain height
        const parentContainer = scrollableArea.closest('.flex.flex-col.min-w-0.bg-white');
        if (parentContainer) {
          // Get the root Interview modal container to calculate available height
          const rootModal = parentContainer.closest('.interview-modal-wrapper');
          if (rootModal) {
            const rootRect = rootModal.getBoundingClientRect();
            const rootHeight = rootRect.height;

            if (rootHeight > 0) {
              // Get header height
              const header = parentContainer.querySelector('.flex-shrink-0');
              const headerHeight = header ? header.getBoundingClientRect().height : 0;

              // Calculate available height for scrollable area
              const availableHeight = rootHeight - headerHeight;

              if (availableHeight > 0) {
                // Set explicit height on parent container first
                parentContainer.style.height = `${rootHeight}px`;
                parentContainer.style.maxHeight = `${rootHeight}px`;
                parentContainer.style.minHeight = '0';

                // Then set height on scrollable area
                scrollableArea.style.height = `${availableHeight}px`;
                scrollableArea.style.maxHeight = `${availableHeight}px`;
                scrollableArea.style.minHeight = '0';
                scrollableArea.style.flex = '1 1 0%';
              }
            }
          }

          // Fallback: use parent container's height directly
          const parentRect = parentContainer.getBoundingClientRect();
          if (parentRect.height > 0) {
            const header = parentContainer.querySelector('.flex-shrink-0');
            const headerHeight = header ? header.getBoundingClientRect().height : 0;
            const availableHeight = parentRect.height - headerHeight;

            if (availableHeight > 0 && !scrollableArea.style.height) {
              scrollableArea.style.height = `${availableHeight}px`;
              scrollableArea.style.maxHeight = `${availableHeight}px`;
            }
          }
        }

        // Ensure the scrollable area has proper constraints
        scrollableArea.style.overscrollBehavior = 'contain';
        scrollableArea.style.overscrollBehaviorY = 'contain';
        scrollableArea.style.overflowY = 'auto';

        // Force a reflow to ensure styles are applied
        void scrollableArea.offsetHeight;

        // Add wheel event listener to prevent parent scroll
        handleWheel = (e) => {
          const { scrollTop, scrollHeight, clientHeight } = scrollableArea;
          const isAtTop = scrollTop <= 0;
          const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

          // If scrolling within bounds, prevent bubbling to parent
          if (!(isAtTop && e.deltaY < 0) && !(isAtBottom && e.deltaY > 0)) {
            e.stopPropagation();
          }
        };

        scrollableArea.addEventListener('wheel', handleWheel, { passive: false });
      } else {
        console.warn('Could not find Interview modal scrollable area');
      }
    }, 400);

    return () => {
      clearTimeout(timer);
      if (scrollableArea && handleWheel) {
        scrollableArea.removeEventListener('wheel', handleWheel);
      }
    };
  }, [showInterviewModal, createStep]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      setOpenActionsDropdown(null);
      setActionMenuPosition(null);
      // The create menu, unlike the row menus, is not portalled — so it has to exclude its own
      // subtree or the same click that opens it immediately closes it again.
      if (!e.target.closest?.('[data-create-menu-container]')) {
        setShowCreateMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const filteredScreenings = screenings.filter((s) => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;
    return (s.name || '').toLowerCase().includes(term);
  });

  const SkeletonBlock = ({ className = '' }) => (
    <div className={`animate-pulse rounded-md bg-gray-200/90 ${className}`} />
  );

  const tableSkeletonRows = 5;

  const generateResumeCriteriaPreview = useCallback(async () => {
    if (!jobId) return [];
    setResumeCriteriaCreating(true);
    setResumeCriteriaCreateError('');
    try {
      const res = await axios.post('/api/screenings/generate-criteria-preview', { jobId });
      const nextCriteria = Array.isArray(res.data?.criteria) ? res.data.criteria : [];
      setResumeScoringCriteria(nextCriteria);
      if (nextCriteria.length === 0) {
        setResumeCriteriaCreateError('No criteria were generated. Please try again.');
      }
      return nextCriteria;
    } catch (e) {
      const message = e.response?.data?.message || 'Failed to generate criteria';
      setResumeCriteriaCreateError(message);
      toast.error(message);
      return [];
    } finally {
      setResumeCriteriaCreating(false);
    }
  }, [jobId]);

  const handleCreateScreening = async () => {
    if (!draftName.trim()) return;

    // Validate components are configured
    if (includeAssessment && !assessmentConfigured) {
      toast.error('Please configure the Assessment before creating the screening');
      return;
    }
    if (includeInterview && !interviewConfigured) {
      toast.error('Please configure the Interview before creating the screening');
      return;
    }
    if (includeDocumentCollection && (!Array.isArray(requestedDocuments) || requestedDocuments.length === 0)) {
      toast.error('Please select at least one document to collect');
      return;
    }


    try {
      setCreating(true);
      const res = await axios.post(`/api/screenings/job/${jobId}`, {
        name: draftName.trim(),
        description: draftDescription.trim(),
        hasResumeScoring: includeResumeScoring,
        hasAssessment: includeAssessment,
        hasInterview: includeInterview,
        includeDocumentCollection: includeDocumentCollection,
        requestedDocuments: requestedDocuments,
        enableDocumentVerification: enableDocumentVerification,
        includeApplicationForm: isApplicationFormEnabled && includeApplicationForm,

        assessmentId: configuredAssessmentId || null,
        interviewId: configuredInterviewId || null,
        resumeScoringCriteria: includeResumeScoring ? resumeScoringCriteria : undefined,
        journeyAutomation: {
          enabled: journeyAutomationEnabled,
          resumeMinScore: journeyAutomationResumeMinScore,
          assessmentMinScore: journeyAutomationAssessmentMinScore,
        },
        enableArmSalaryRequest: enableArmSalaryRequest,
      });
      const newScreening = res.data.screening;
      await fetchScreenings();
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });

      // Reset all state
      setDraftName('');
      setDraftDescription('');
      setIncludeResumeScoring(true);
      setResumeScoringCriteria([]);
      setIncludeAssessment(false);
      setIncludeInterview(false);
      setIncludeDocumentCollection(false);
      setRequestedDocuments([]);
      setIncludeApplicationForm(false);
      setEnableDocumentVerification(false);

      setJourneyAutomationEnabled(defaultJourneyAutomation.enabled || false);
      setJourneyAutomationResumeMinScore(defaultJourneyAutomation.resumeMinScore ?? 60);
      setJourneyAutomationAssessmentMinScore(defaultJourneyAutomation.assessmentMinScore ?? 60);
      setEnableArmSalaryRequest(false);
      setCreateStep(1);
      setConfiguredAssessmentId(null);
      setConfiguredAssessmentDuration(null);
      setConfiguredInterviewId(null);
      setAssessmentConfigured(false);
      setInterviewConfigured(false);
      setShowAssessmentModal(false);
      setShowInterviewModal(false);
      setAssessmentSource(null);
      setInterviewSource(null);
      setIsCreateOpen(false);
      toast.success('Screening created');
      fetchScreenings();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to create screening');
    } finally {
      setCreating(false);
    }
  };

  const handleDiscardConfiguredSteps = async () => {
    const hasConfiguredAssessment = assessmentConfigured && configuredAssessmentId;
    const hasConfiguredInterview = interviewConfigured && configuredInterviewId;

    if (hasConfiguredAssessment || hasConfiguredInterview) {
      try {
        if (hasConfiguredAssessment) {
          await axios.delete(`/api/assessments/${configuredAssessmentId}`);
        }
        if (hasConfiguredInterview) {
          await axios.delete(`/api/ai-interviews/${configuredInterviewId}`);
        }
      } catch (error) {
        console.error('Error discarding configured screening components:', error);
        toast.error('Could not discard configured steps. Please try again.');
        throw error;
      }
    }
  };

  const discardConfiguredAssessment = async () => {
    if (configuredAssessmentId) {
      try {
        await axios.delete(`/api/assessments/${configuredAssessmentId}`);
      } catch (error) {
        console.error('Error discarding configured assessment:', error);
        toast.error('Could not discard the assessment. Please try again.');
        throw error;
      }
    }

    setConfiguredAssessmentId(null);
    setConfiguredAssessmentDuration(null);
    setAssessmentConfigured(false);
    setAssessmentSource(null);
    setShowAssessmentModal(false);
  };

  const discardConfiguredInterview = async () => {
    if (configuredInterviewId) {
      try {
        await axios.delete(`/api/ai-interviews/${configuredInterviewId}`);
      } catch (error) {
        console.error('Error discarding configured interview:', error);
        toast.error('Could not discard the interview. Please try again.');
        throw error;
      }
    }

    setConfiguredInterviewId(null);
    setInterviewConfigured(false);
    setInterviewSource(null);
    setShowInterviewModal(false);
    setShowInterviewLibraryPicker(false);
  };

  const handleResetInterviewConfiguration = async () => {
    // If there is no configured interview yet, just return the user to the source selection step
    if (!configuredInterviewId) {
      setInterviewConfigured(false);
      setInterviewSource(null);
      setShowInterviewModal(false);
      setShowInterviewLibraryPicker(false);
      setCreateStep(includeAssessment ? (4 + rsOff) : (3 + rsOff));
      return;
    }

    try {
      await axios.delete(`/api/ai-interviews/${configuredInterviewId}`);
    } catch (error) {
      console.error('Error discarding configured interview:', error);
      toast.error('Could not discard the interview. Please try again.');
      return;
    }

    setConfiguredInterviewId(null);
    setInterviewConfigured(false);
    setInterviewSource(null);
    setShowInterviewModal(false);
    setShowInterviewLibraryPicker(false);
    setCreateStep(includeAssessment ? (4 + rsOff + dcOff) : (3 + rsOff + dcOff));
    toast.success('Interview removed. You can choose how to set it up again.');
  };
 
  const handleCancelCreateFlow = () => {
    setShowDiscardConfirm(true);
  };
 
  const handleStep2Next = async () => {
    const hasComponents =
      includeResumeScoring || includeAssessment || includeInterview || includeDocumentCollection || includeApplicationForm;
    if (!hasComponents) {
      toast.error('Please select at least one component');
      return;
    }

    if (includeResumeScoring) {
      // Generate criteria only when user explicitly proceeds.
      const existingCriteria = Array.isArray(resumeScoringCriteria) ? resumeScoringCriteria : [];
      const generatedCriteria =
        existingCriteria.length === 0 && !resumeCriteriaCreating
          ? await generateResumeCriteriaPreview()
          : existingCriteria;

      if (generatedCriteria.length === 0) {
        // Stay on this step; user can retry by clicking Next again.
        return;
      }
      setCreateStep(3);
      return;
    }

    if (includeDocumentCollection) {
      setCreateStep(3 + rsOff);
      return;
    }

    if (includeAssessment) {
      setCreateStep(3 + rsOff + dcOff);
      return;
    }

    if (includeInterview) {
      setCreateStep(3 + rsOff + dcOff);
      setShowInterviewModal(false);
      setShowInterviewLibraryPicker(false);
      setInterviewSource(null);
    } else {
      setCreateStep(reviewStep);
    }
  };

  const requestRemoveMethod = (key, { title, message, onConfirm }) => {
    setMethodRemoveConfirm({ key, title, message, onConfirm });
  };

  const handleResumeCriteriaNext = () => {
    if (includeDocumentCollection) {
      setCreateStep(3 + rsOff);
    } else if (includeAssessment) {
      setCreateStep(3 + rsOff + dcOff);
    } else if (includeInterview) {
      setCreateStep(3 + rsOff + dcOff);
      setShowInterviewModal(false);
      setShowInterviewLibraryPicker(false);
      setInterviewSource(null);
    } else {
      setCreateStep(reviewStep);
    }
  };

  const handleDocumentCollectionNext = () => {
    if (includeAssessment) {
      setCreateStep(3 + rsOff + dcOff);
    } else if (includeInterview) {
      setCreateStep(3 + rsOff + dcOff);
      setShowInterviewModal(false);
      setShowInterviewLibraryPicker(false);
      setInterviewSource(null);
    } else {
      setCreateStep(reviewStep);
    }
  };
 
  const handleStep3Next = () => {
    // Validate assessment source selection
    if (!assessmentSource) {
      toast.error('Please select how you want to add the assessment');
      return;
    }
 
    // Stay on Step 3 (Assessment setup) and open AssessmentCreateFlowModal
    setShowAssessmentModal(true);
  };
 
  const handleStep4Next = () => {
    // Validate that all selected components are configured
    if (includeAssessment && !assessmentConfigured) {
      toast.error('Please configure the Assessment before proceeding');
      return;
    }
    if (includeInterview && !interviewConfigured) {
      toast.error('Please configure the Interview before proceeding');
      return;
    }
 
    // All components configured, proceed to Review
    setCreateStep(5 + rsOff + dcOff);
  };
 
  const handleAssessmentComplete = (result) => {
    const didCreate = typeof result === 'object' && result?.didCreate;
    const newAssessmentId = typeof result === 'object' ? result?.assessmentId : null;
    const duration = typeof result === 'object' ? result?.duration : null;
 
    if (didCreate && newAssessmentId) {
      setConfiguredAssessmentId(newAssessmentId);
      setConfiguredAssessmentDuration(duration != null ? Number(duration) : null);
      setAssessmentConfigured(true);
      setShowAssessmentModal(false);
      toast.success('Assessment configured');
 
      if (includeInterview && !interviewConfigured) {
        setCreateStep(4 + rsOff + dcOff);
        setShowInterviewModal(false);
        setInterviewSource(null);
      } else {
        setCreateStep(4 + rsOff + dcOff);
      }
    } else {
      setShowAssessmentModal(false);
    }
  };

  const handleInterviewSourceNext = () => {
    if (!interviewSource) {
      toast.error('Please select how you want to add the interview');
      return;
    }
    if (interviewSource === 'library') {
      setShowInterviewModal(false);
      setShowInterviewLibraryPicker(true);
      return;
    }
    setShowInterviewLibraryPicker(false);
    setShowInterviewModal(true);
  };

  const handlePickInterviewTemplate = async (templateId, options = {}) => {
    try {
      const template = await axios.get(`/api/library/ai-interviews/${templateId}`).then((r) => r.data);
      const payload = buildInterviewFromLibraryTemplate(template, {
        templateId,
        jobId,
        fallbackTitle: draftName?.trim(),
      });
      if (!payload) {
        toast.error('This interview template has no questions.');
        return;
      }

      const res = await axios.post('/api/ai-interviews/create', payload);
      const createdId = res.data?.interview?._id;
      if (!createdId) {
        toast.error('Failed to create interview from library.');
        return;
      }
      if (options?.proctoring) {
        try {
          await axios.post(`/api/ai-interviews/${createdId}/proctoring/config`, {
            browserActivityMonitoring: Boolean(options.proctoring.browserActivityMonitoring),
            responseDelayTracking: Boolean(options.proctoring.responseDelayTracking),
            speechPatternAnalysis: Boolean(options.proctoring.speechPatternAnalysis),
            transcriptAnalysis: Boolean(options.proctoring.transcriptAnalysis),
            multiSpeakerDetection: Boolean(options.proctoring.multiSpeakerDetection),
            voiceConsistencyCheck: Boolean(options.proctoring.voiceConsistencyCheck),
            randomInterruptQuestions: Boolean(options.proctoring.randomInterruptQuestions),
            facePresenceDetection: Boolean(options.proctoring.facePresenceDetection),
            multipleFaceDetection: Boolean(options.proctoring.multipleFaceDetection),
            faceIdentityConsistency: Boolean(options.proctoring.faceIdentityConsistency),
            jobId: jobId || undefined,
          });
        } catch (proctoringError) {
          console.error('Error saving interview proctoring settings:', proctoringError);
          toast.error('Interview was created, but proctoring settings could not be saved.');
        }
      }
      setConfiguredInterviewId(createdId);
      setInterviewConfigured(true);
      setShowInterviewModal(false);
      setShowInterviewLibraryPicker(false);
      toast.success('Interview selected from library');
      setCreateStep(includeAssessment ? (5 + rsOff + dcOff) : (4 + rsOff + dcOff));
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to use interview from library');
    }
  };

  const handleInterviewComplete = (createdInterviewId) => {
    if (createdInterviewId) {
      setConfiguredInterviewId(createdInterviewId);
      setInterviewConfigured(true);
      setShowInterviewModal(false);
      setShowInterviewLibraryPicker(false);

      // Review: Step 5 when both Assessment + Interview, Step 4 when only Interview
      setCreateStep(includeAssessment ? (5 + rsOff + dcOff) : (4 + rsOff + dcOff));
    } else {
      setShowInterviewModal(false);
      setShowInterviewLibraryPicker(false);
    }
  };

  const handleSkipConfiguration = () => {
    // Allow creating screening without configuring Assessment/Interview
    // They can be configured later from the detail page
    handleCreateScreening();
  };

  const handleDownloadJobPdf = useCallback(() => {
    if (!job) return;

    try {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const margin = 15;
      let y = 20;

      const safeTitle = (job.title || 'Job Description')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      doc.setFontSize(16);
      doc.text(safeTitle || 'Job Description', margin, y);
      y += 8;

      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
      y += 8;

      const addSection = (title, body) => {
        if (!body) return;
        doc.setFontSize(11);
        doc.setTextColor(60);
        doc.text(title, margin, y);
        y += 5;
        doc.setFontSize(10);
        doc.setTextColor(40);
        const lines = doc.splitTextToSize(body, 180);
        lines.forEach((line) => {
          if (y > 280) {
            doc.addPage();
            y = 20;
          }
          doc.text(line, margin, y);
          y += 5;
        });
        y += 4;
      };

      // Overview
      addSection('Overview', job.description || 'No description available for this job.');

      // Key details
      const parts = [];
      const hasLocation =
        (Array.isArray(job.location) ? job.location.length > 0 : job.location) || job.locationType;
      if (hasLocation) {
        parts.push(`Location: ${formatLocationDisplay(job.location, job.locationType)}`);
      }
      if (job.experienceMin !== undefined || job.experienceMax !== undefined) {
        parts.push(`Experience: ${job.experienceMin ?? '0'}–${job.experienceMax ?? '0'} years`);
      }
      if (job.salaryMin !== undefined || job.salaryMax !== undefined) {
        parts.push(
          `Compensation: ${job.salaryCurrency || 'USD'} ${job.salaryMin ?? 0}–${job.salaryMax ?? 0}`
        );
      }
      if (
        job.candidateQualification ||
        job.genderPreference ||
        job.ageMin !== undefined ||
        job.ageMax !== undefined
      ) {
        const profileLines = [];
        if (job.candidateQualification) {
          profileLines.push(job.candidateQualification);
        }
        if (job.ageMin !== undefined || job.ageMax !== undefined) {
          profileLines.push(`Age: ${job.ageMin ?? '—'}–${job.ageMax ?? '—'}`);
        }
        if (job.genderPreference && job.genderPreference !== 'any') {
          profileLines.push(`Gender preference: ${job.genderPreference}`);
        }
        parts.push(`Candidate profile: ${profileLines.join(' | ')}`);
      }
      if (parts.length > 0) {
        addSection('Key details', parts.join('\n'));
      }

      if (job.keyResponsibilities) {
        addSection('Key responsibilities', job.keyResponsibilities);
      }

      if (job.keyAttributes) {
        addSection('Key attributes', job.keyAttributes);
      }

      if (Array.isArray(job.skills) && job.skills.length > 0) {
        addSection('Skills', job.skills.join(', '));
      }

      const fileSafeTitle = (job.title || 'Job_Description')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '_')
        .trim();
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${fileSafeTitle || 'Job_Description'}_JD_${dateStr}.pdf`;

      doc.save(filename);
      toast.success(`JD downloaded as ${filename}`);
    } catch (err) {
      console.error('Failed to generate JD PDF:', err);
      toast.error('Failed to download job description PDF');
    }
  }, [job]);

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) return 'less than a minute ago';
    if (diffMinutes < 60)
      return diffMinutes === 1 ? 'about 1 minute ago' : `about ${diffMinutes} minutes ago`;
    if (diffHours < 24)
      return diffHours === 1 ? 'about 1 hour ago' : `about ${diffHours} hours ago`;
    if (diffDays < 30) return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    if (diffMonths < 12) return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
    return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
  };

  const handleOpenScreening = (screeningId, screeningData) => {
    axios
      .patch(`/api/screenings/${screeningId}`, {
        lastAccessedAt: new Date().toISOString(),
      })
      .catch((err) => {
        // Silently fail - don't block navigation
        console.error('Failed to update last accessed time:', err);
      });
    navigate(`/dashboard/job-posts/jobs/${jobId}/screenings/${screeningId}`, {
      state: screeningData ? { screening: screeningData } : undefined,
    });
  };

  const handleDeleteScreening = async (screening) => {
    if (!screening) return;
    try {
      setDeletingId(screening._id);
      await axios.delete(`/api/screenings/${screening._id}`);
      setScreenings((prev) => prev.filter((s) => s._id !== screening._id));
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
      fetchScreenings();
      toast.success('Screening deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete screening');
    } finally {
      setDeletingId(null);
      setScreeningPendingDelete(null);
    }
  };

  /**
   * Hide header/footer/outer padding only when an embedded flow is actually visible for this step.
   * Previously, any `showInterviewModal` on step 3 hid chrome even when `includeAssessment` was true
   * (interview UI lives on step 4 in that case), which broke the UI after using the stepper to revisit
   * "Screening methods" and returning to assessment source selection.
   */
  const hideCreateFlowEmbeddedChrome =
    (createStep === 3 + rsOff + dcOff && includeAssessment && showAssessmentModal) ||
    (createStep === 3 + rsOff + dcOff &&
      includeInterview &&
      !includeAssessment &&
      (showInterviewModal || showInterviewLibraryPicker)) ||
    (createStep === 4 + rsOff + dcOff && (showInterviewModal || showInterviewLibraryPicker));

  const goToCreateStepFromStepper = (step) => {
    setCreateStep(step);
    if (step <= 2 || (includeResumeScoring && step === 3) || (includeDocumentCollection && step === 3 + rsOff)) {
      setShowAssessmentModal(false);
      setShowInterviewModal(false);
      setShowInterviewLibraryPicker(false);
    } else if (step === 3 + rsOff + dcOff) {
      if (includeAssessment) {
        setShowInterviewModal(false);
        setShowInterviewLibraryPicker(false);
      }
      if (includeInterview && !includeAssessment) {
        setShowAssessmentModal(false);
        setShowInterviewModal(false);
        setShowInterviewLibraryPicker(false);
      }
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F7F9FC] px-4 sm:px-6 lg:px-8 desktop:px-10 pt-6 pb-12">
      <style>{embeddedModalStyles}</style>
      {/* Job header — aligned with job-post (ResultsAnalytics) */}
      <div className="mb-6">
        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <button
              type="button"
              onClick={() => navigate('/dashboard/job-post')}
              className="inline-flex items-center justify-center rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              aria-label="Back to job posts"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            {jobLoading && !job ? (
              <SkeletonBlock className="h-9 w-[24rem] max-w-full" />
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">
                  {job?.title || 'Screenings for job'}
                </h1>
                {isGoColors && job && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {job.storeFormat === 'LFS' ? 'LFS' : 'EBO'}
                  </span>
                )}
              </div>
            )}
          </div>
          {jobError && (
            <p className="text-xs text-red-500">Could not load job details: {jobError}</p>
          )}
          {jobLoading && !job ? (
            <div className="flex items-start gap-2 mt-1 pb-4 border-b border-gray-200">
              <div className="flex-1 min-w-0">
                <SkeletonBlock className="h-4 w-full mb-2" />
                <SkeletonBlock className="h-4 w-5/6" />
              </div>
              <SkeletonBlock className="h-8 w-8 rounded-lg flex-shrink-0" />
            </div>
          ) : job ? (
            <div className="flex items-start gap-2 mt-1 pb-4 border-b border-gray-200">
              <p className="flex-1 text-sm text-gray-600 line-clamp-2 min-w-0">
                {job.description || 'No description'}
              </p>
              <button
                type="button"
                onClick={() => setShowJobDescriptionModal(true)}
                className="flex-shrink-0 inline-flex items-center justify-center rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                aria-label="View full job details"
              >
                <DocumentTextIcon className="h-4 w-4" />
              </button>
            </div>
          ) : null}
          {!job && !jobLoading && !jobError && (
            <div className="pb-4 border-b border-gray-200">
              <p className="text-sm text-gray-600 mt-1">
                Design different screening flows for this job and track candidates end to end.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      <div className="mb-6">
        <nav className="flex gap-1 border-b border-gray-200" aria-label="Job tabs">
          <button
            onClick={() => { setActivePageTab('screening'); navigate(`/dashboard/job-posts/jobs/${jobId}/screenings`); }}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 -mb-px ${activePageTab === 'screening'
                ? 'border-[#007BFF] text-[#007BFF]'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
          >
            Screening
          </button>
          <button
            onClick={() => { setActivePageTab('sourcing'); navigate(`/dashboard/job-posts/jobs/${jobId}/sourcing`); }}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 -mb-px ${activePageTab === 'sourcing'
                ? 'border-[#007BFF] text-[#007BFF]'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
          >
            Sourcing
          </button>
        </nav>
      </div>

      {activePageTab === 'sourcing' && (
        <div>
          {!isSourcingEnabled ? (
            <div className="w-full mt-4 bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
              {/* Premium Gradient Compact Header */}
              <div className="py-6 px-8 text-center border-b border-gray-100 bg-gradient-to-br from-[#007BFF]/5 via-[#F7F9FC] to-white relative overflow-hidden">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold text-blue-700 bg-blue-50 tracking-wider uppercase mb-2.5 border border-blue-100">
                  <SparklesIcon className="h-3 w-3" />
                  Premium Add-On
                </span>
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-2">
                  Unlock AI Candidate Sourcing
                </h2>
                <p className="text-sm text-gray-600 max-w-2xl mx-auto leading-normal">
                  Automatically scan professional databases, score candidates, and build your active talent pipeline on autopilot.
                </p>
              </div>

              {/* Horizontal / Compact Benefit Columns */}
              <div className="p-6 bg-white grid grid-cols-1 md:grid-cols-3 gap-6 border-b border-gray-50">
                {/* Benefit 1 */}
                <div className="flex items-start gap-3 p-3.5 rounded-lg border border-gray-100 bg-gray-50/20 hover:bg-gray-50/50 transition-colors">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-md shrink-0">
                    <SparklesIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-gray-950">1. Automated Discovery</h4>
                    <p className="text-xs text-gray-500 mt-1 leading-normal">
                      AI scours global networks to curate candidate profiles matching your exact job requirements.
                    </p>
                  </div>
                </div>

                {/* Benefit 2 */}
                <div className="flex items-start gap-3 p-3.5 rounded-lg border border-gray-100 bg-gray-50/20 hover:bg-gray-50/50 transition-colors">
                  <div className="p-2 bg-emerald-50 text-emerald-600 rounded-md shrink-0">
                    <MagnifyingGlassIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-gray-950">2. Smart Fit Scoring</h4>
                    <p className="text-xs text-gray-500 mt-1 leading-normal">
                      Instantly rates all candidate matches from 0 to 100 with comprehensive matching intelligence.
                    </p>
                  </div>
                </div>

                {/* Benefit 3 */}
                <div className="flex items-start gap-3 p-3.5 rounded-lg border border-gray-100 bg-gray-50/20 hover:bg-gray-50/50 transition-colors">
                  <div className="p-2 bg-purple-50 text-purple-600 rounded-md shrink-0">
                    <PlusIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-gray-950">3. 1-Click Screening</h4>
                    <p className="text-xs text-gray-500 mt-1 leading-normal">
                      Push matching profiles directly into automated resume scoring, custom tests, or AI interviews.
                    </p>
                  </div>
                </div>
              </div>

              {/* Space-efficient Horizontal CTA Footer */}
              <div className="py-4 px-8 bg-gray-50/80 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <p className="text-sm font-bold text-gray-800">Ready to supercharge your hiring pipeline?</p>
                  <p className="text-xs text-gray-500 mt-0.5">Contact our sales team to instantly activate AI Candidate Sourcing.</p>
                </div>
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      const orgName = user?.company || user?.name || 'Our Organisation';
                      const email = 'sales@talentcrux.ai';
                      const subject = encodeURIComponent(`Request Activation: AI Candidate Sourcing — ${orgName}`);
                      const body = encodeURIComponent(
                        `Hello TalentCrux Sales Team,\n\nI would love to activate the premium AI Candidate Sourcing feature for our organisation (${orgName}). Please send us details on how to get started!\n\nBest regards,\n${user?.name || 'Recruiting Team'}`
                      );
                      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
                    }}
                    className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-xs rounded-lg shadow-sm hover:shadow-md transition-all duration-150 flex items-center gap-2 cursor-pointer"
                  >
                    <ChatBubbleLeftRightIcon className="h-4 w-4" />
                    <span>Contact Sales</span>
                  </button>
                  <span className="text-[10px] text-gray-400 font-medium select-none">
                    Our sales team will activate it for you
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div>
          {/* Sourcing search bar */}
          <div className="">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 transition-colors duration-200 pointer-events-none" />
              <input
                type="text"
                placeholder="Search sourcing sessions..."
                value={sourcingSearch}
                onChange={(e) => setSourcingSearch(e.target.value)}
                className={`w-full pl-12 py-3 bg-white border-0 rounded-lg shadow-sm text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-colors duration-200 ease-out ${sourcingSearch ? 'pr-24' : 'pr-4'}`}
              />
              {sourcingSearch && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium tabular-nums">
                  {activeSourcingSessions.filter(s => (s.title || '').toLowerCase().includes(sourcingSearch.toLowerCase())).length} result{activeSourcingSessions.filter(s => (s.title || '').toLowerCase().includes(sourcingSearch.toLowerCase())).length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Sourcing header */}
          <div className="pl-6 pr-0 py-4 bg-[#F7F9FC] mb-0">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-[#242124]">All Sourcing Sessions</h2>
              <button
                type="button"
                onClick={() => { setSelectedSourcingSession(null); setShowSourcingModal(true); }}
                className="px-5 py-2.5 bg-transparent border-2 border-gray-900 text-gray-900 rounded-lg hover:bg-[#007BFF] hover:border-[#007BFF] hover:text-white flex items-center gap-2 font-semibold transition-all duration-200 text-sm shrink-0"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Create sourcing</span>
              </button>
            </div>
          </div>

          {/* Table */}
          {sourcingLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#007BFF]" />
            </div>
          ) : sourcingError ? (
            <div className="text-center py-16 text-sm text-red-500">{sourcingError}</div>
          ) : activeSourcingSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <SparklesIcon className="h-10 w-10 text-gray-300" />
              <p className="text-sm font-semibold text-gray-500">No sourcing sessions yet</p>
              <p className="text-xs text-gray-400">Click "Create sourcing" to start finding candidates for this job.</p>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden border border-gray-100 shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#007BFF] text-white">
                    <th className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-center font-semibold text-xs uppercase tracking-wider">Candidates Found</th>
                    <th className="px-4 py-3 text-center font-semibold text-xs uppercase tracking-wider">{isSamtaGroup ? 'Shortlisted' : 'Selected'}</th>
                    <th className="px-4 py-3 text-center font-semibold text-xs uppercase tracking-wider">Creation Date</th>
                    <th className="px-4 py-3 text-center font-semibold text-xs uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {activeSourcingSessions.filter(s => !sourcingSearch || (s.title || '').toLowerCase().includes(sourcingSearch.toLowerCase())).map((session) => (
                    <tr key={session._id} className="hover:bg-[#F7F9FC] transition-colors cursor-pointer group" onClick={() => navigate(`/dashboard/job-posts/jobs/${jobId}/sourcing/${session._id}`, { state: { session } })}>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-[#242124]">{session.title || 'Untitled Search'}</span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-700">{(session.totalFound || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-center text-gray-700">{(session.shortlistedIds?.length || 0)}</td>
                      <td className="px-4 py-3 text-center text-gray-500 text-xs">
                        {new Date(session.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                      </td>
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleDeleteSourcingSession(session._id)}
                          disabled={deletingSourcingId === session._id}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                          title="Delete session"
                        >
                          {deletingSourcingId === session._id
                            ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-red-500" />
                            : <TrashIcon className="h-4 w-4" />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      </div>
    )}

      {/* Sourcing Agent — Create mode (60vw/80vh centred modal) */}
      {showSourcingModal && !selectedSourcingSession && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px] flex items-center justify-center">
          <Suspense fallback={<div className="w-[60vw] h-[80vh] flex items-center justify-center bg-white rounded-2xl"><div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#007BFF]" /></div>}>
            <SourcingAgent
              jobId={jobId}
              mode="create"
              jobContext={job ? {
                title: job.title,
                location: job.location,
                locationType: job.locationType,
                experienceMin: job.experienceMin,
                experienceMax: job.experienceMax,
                description: job.description,
                keyResponsibilities: job.keyResponsibilities,
                candidateQualification: job.candidateQualification,
                skills: job.skills
              } : null}
              onClose={() => { setShowSourcingModal(false); setSelectedSourcingSession(null); setTimeout(() => fetchSourcingSessions(), 500); }}
            />
          </Suspense>
        </div>
      )}

      {activePageTab === 'screening' && (
        <>
                {/* Search bar */}
                {!screeningsError && (
                  <div className="">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 transition-colors duration-200 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Search screenings..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        disabled={screeningsLoading}
                        className={`w-full pl-12 py-3 bg-white border-0 rounded-lg shadow-sm text-sm
                        placeholder:text-gray-400
                        focus:outline-none focus:ring-2 focus:ring-blue-500/30
                        disabled:opacity-70 disabled:cursor-not-allowed
                        transition-colors duration-200 ease-out
                        ${searchTerm ? 'pr-24' : 'pr-4'}`}
                      />
                      {searchTerm && (
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium tabular-nums">
                          {filteredScreenings.length} result{filteredScreenings.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* All Screenings Header — screening tab only */}
                <div className="pl-6 pr-0 py-4 bg-[#F7F9FC] mb-0">
                  <div className="flex items-center justify-between">
                    <h2 className="text-base font-semibold text-[#242124]">All Screenings</h2>
                    <div className="flex items-center gap-2 shrink-0">
                      {/* Four buttons stood here, three of them the same outline-blue, and two
                          of them — WhatsApp Agent and Qualifying Agent — were *kinds of
                          screening* sitting as siblings of the button that creates screenings.
                          That is what made the header read as a lot to take in at once. Now:
                          one settings action, and one create action that owns every screening
                          type. Automations stays separate because it acts on rounds that
                          already exist rather than making one. */}
                      {isGoColors && jobId && <GoColorsPayrollDownloadMenu jobId={jobId} job={job} />}
                      {/* Deliberately unlabelled: an internal control for connecting screenings
                          of one hiring cycle so the duplicate check treats them as one. */}
                      <button
                        type="button"
                        onClick={() => setShowConnectedScreenings(true)}
                        data-testid="connected-screenings-btn"
                        aria-label="Connected screenings"
                        className="p-2 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-colors"
                      >
                        <LinkIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAutomations(true)}
                        data-testid="automations-btn"
                        className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:border-[#007BFF]/50 hover:text-[#007BFF] hover:bg-[#007BFF]/5 flex items-center gap-2 font-semibold transition-all duration-200 text-sm"
                      >
                        <BoltIcon className="h-5 w-5" />
                        <span>Automations</span>
                      </button>

                      <div className="relative" data-create-menu-container>
                        <button
                          type="button"
                          data-testid="create-screening-btn"
                          onClick={() => {
                            // With no agent features licensed there is only one thing this
                            // button can do, so it does it rather than opening a menu of one.
                            if (!isWhatsAppAgentEnabled && !isQualifyingAgentEnabled) {
                              setIsCreateOpen(true);
                              return;
                            }
                            setShowCreateMenu((v) => !v);
                          }}
                          className="px-5 py-2.5 bg-transparent border-2 border-gray-900 text-gray-900 rounded-lg hover:bg-[#007BFF] hover:border-[#007BFF] hover:text-white flex items-center gap-2 font-semibold transition-all duration-200 text-sm"
                        >
                          <PlusIcon className="h-5 w-5" />
                          <span>Create agents</span>
                          {(isWhatsAppAgentEnabled || isQualifyingAgentEnabled) && (
                            <ChevronDownIcon className="h-4 w-4" />
                          )}
                        </button>

                        {showCreateMenu && (
                          <div className="absolute right-0 z-30 mt-2 w-[19rem] overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
                            <CreateMenuItem
                              Icon={SparklesIcon}
                              iconClass="text-indigo-500"
                              title="Screening Agent"
                              desc="Resume scoring, assessment, AI interview or documents."
                              onClick={() => {
                                setShowCreateMenu(false);
                                setIsCreateOpen(true);
                              }}
                            />
                            {isQualifyingAgentEnabled && (
                              <CreateMenuItem
                                Icon={PhoneIcon}
                                iconClass="text-[#007BFF]"
                                title="Qualifying Agent"
                                desc="Pre-screen by phone or WhatsApp before anything else."
                                testId="qualifying-agent-btn"
                                onClick={() => {
                                  setShowCreateMenu(false);
                                  setQualifyingAgentEditing(null);
                                  setShowQualifyingAgent(true);
                                }}
                              />
                            )}
                            {isWhatsAppAgentEnabled && (
                              <CreateMenuItem
                                Icon={ChatBubbleLeftRightIcon}
                                iconClass="text-[#25D366]"
                                title="WhatsApp Agent"
                                desc="Run the whole screening conversation over WhatsApp."
                                testId="whatsapp-agent-btn"
                                onClick={() => {
                                  setShowCreateMenu(false);
                                  setShowWhatsAppAgent(true);
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <AutomationsModal
                  isOpen={showAutomations}
                  onClose={() => setShowAutomations(false)}
                  jobId={jobId}
                  onAppliedChanges={() => fetchScreenings()}
                />
                <ConnectedScreeningsModal
                  isOpen={showConnectedScreenings}
                  onClose={() => setShowConnectedScreenings(false)}
                  jobId={jobId}
                />
                {isWhatsAppAgentEnabled && (
                  <WhatsAppAgentModal
                    isOpen={showWhatsAppAgent}
                    onClose={() => setShowWhatsAppAgent(false)}
                    jobId={jobId}
                    onSaved={() => fetchScreenings()}
                  />
                )}
                {isQualifyingAgentEnabled && (
                  <QualifyingAgentModal
                    isOpen={showQualifyingAgent}
                    onClose={() => {
                      setShowQualifyingAgent(false);
                      setQualifyingAgentEditing(null);
                    }}
                    jobId={jobId}
                    job={job}
                    screening={qualifyingAgentEditing}
                    onSaved={() => fetchScreenings()}
                  />
                )}

        {/* Screenings table card */}
        <div className="bg-[#F7F9FC] rounded-xl shadow-sm overflow-hidden">
          {screeningsLoading ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#007BFF]">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider rounded-tl-xl">
                      Name
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">
                      Screening types
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                      Candidates
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">
                      {isSamtaGroup ? 'Shortlisted' : 'Selected'}
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                      Creation date
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider w-14 rounded-tr-xl">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: tableSkeletonRows }).map((_, index) => (
                    <tr
                      key={`screenings-skeleton-row-${index}`}
                      className="bg-white border-b border-l border-r border-gray-300/50"
                    >
                      <td className="px-6 py-4">
                        <SkeletonBlock className="h-4 w-40 max-w-full mb-2" />
                        <SkeletonBlock className="h-3 w-56 max-w-full hidden md:block" />
                      </td>
                      <td className="px-4 py-4 text-center hidden md:table-cell">
                        <div className="flex items-center justify-center gap-1">
                          <SkeletonBlock className="h-6 w-6 rounded-md" />
                          <SkeletonBlock className="h-6 w-6 rounded-md" />
                          <SkeletonBlock className="h-6 w-6 rounded-md" />
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <SkeletonBlock className="h-7 w-10 rounded-md mx-auto" />
                      </td>
                      <td className="px-4 py-4 text-center hidden md:table-cell">
                        <SkeletonBlock className="h-7 w-10 rounded-md mx-auto" />
                      </td>
                      <td className="px-6 py-4 text-center hidden md:table-cell">
                        <SkeletonBlock className="h-4 w-16 mx-auto" />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <SkeletonBlock className="h-8 w-8 rounded-lg ml-auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : screeningsError ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 text-center py-20">
              <p className="text-red-700">{screeningsError}</p>
            </div>
          ) : screenings.length === 0 ? (
            <div className="text-center py-16 px-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <ClipboardDocumentListIcon className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No screenings yet</h3>
              <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                Create your first screening to combine resume scoring with assessments and AI
                interviews for this job.
              </p>
              <button
                type="button"
                data-testid="create-screening-empty-btn"
                onClick={() => setIsCreateOpen(true)}
                className="px-5 py-2.5 bg-[#003153] text-white rounded-lg hover:bg-[#004175] flex items-center gap-2 font-semibold shadow-md mx-auto text-sm"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Create screening</span>
              </button>
            </div>
          ) : filteredScreenings.length === 0 ? (
            <div className="text-center py-16 px-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <FunnelIcon className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No screenings match your search
              </h3>
              <p className="text-gray-500 text-sm">Try adjusting your search criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#007BFF]">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider rounded-tl-xl">
                      Name
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">
                      Screening types
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                      Candidates
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">
                      {isSamtaGroup ? 'Shortlisted' : 'Selected'}
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                      Creation date
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider w-14 rounded-tr-xl">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredScreenings.map((screening) => (
                    <tr
                      key={screening._id}
                      className="group transition-colors bg-white border-b border-l border-r border-gray-300/50 cursor-pointer hover:bg-[#8472B3]/10"
                      onClick={() => handleOpenScreening(screening._id, screening)}
                    >
                      <td className="px-6 py-4">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-[#242124] group-hover:text-[#242124] transition-colors line-clamp-2">
                            {screening.name}
                          </div>
                          {screening.description && (
                            <div className="mt-0.5 text-xs text-[#242124] line-clamp-1 hidden md:block">
                              {screening.description}
                            </div>
                          )}
                          <div className="md:hidden text-xs text-[#242124] mt-0.5">
                            {screening.createdAt
                              ? new Date(screening.createdAt).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: '2-digit',
                              })
                              : '—'}{' '}
                            · {screening.candidateCount ?? 0} candidates ·{' '}
                            {screening.shortlistedCount ?? 0} {isSamtaGroup ? 'shortlisted' : 'selected'}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center hidden md:table-cell">
                        <div className="flex items-center justify-center gap-2">
                           <span
                            title="AI Resume"
                            className={[
                              'inline-flex h-7 w-7 items-center justify-center rounded-md',
                              screening.hasResumeScoring !== false
                                ? 'bg-emerald-100 text-emerald-600'
                                : 'bg-gray-100 text-gray-400',
                            ].join(' ')}
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                          </span>
                          <span
                            title="BGV Documents"
                            className={[
                              'inline-flex h-7 w-7 items-center justify-center rounded-md',
                              screening.includeDocumentCollection
                                ? 'bg-orange-100 text-orange-600'
                                : 'bg-gray-100 text-gray-400',
                            ].join(' ')}
                          >
                            <DocumentArrowUpIcon className="h-4 w-4" />
                          </span>
                          <span
                            title="Assessment"
                            className={[
                              'inline-flex h-7 w-7 items-center justify-center rounded-md',
                              screening.assessmentId ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400',
                            ].join(' ')}
                          >
                            <ClipboardIcon className="h-4 w-4" />
                          </span>
                          <span
                            title="AI Interview"
                            className={[
                              'inline-flex h-7 w-7 items-center justify-center rounded-md',
                              screening.interviewId
                                ? 'bg-purple-100 text-purple-600'
                                : 'bg-gray-100 text-gray-400',
                            ].join(' ')}
                          >
                            <VideoCameraIcon className="h-4 w-4" />
                          </span>
                          {(isWhatsAppAgentEnabled || screening.includeWhatsAppAgent) && (
                            <span
                              title="WhatsApp Agent"
                              className={[
                                'inline-flex h-7 w-7 items-center justify-center rounded-md',
                                screening.includeWhatsAppAgent
                                  ? 'bg-[#25D366]/15 text-[#1c9950]'
                                  : 'bg-gray-100 text-gray-400',
                              ].join(' ')}
                            >
                              <ChatBubbleLeftRightIcon className="h-4 w-4" />
                            </span>
                          )}
                          {(isQualifyingAgentEnabled || screening.includeQualifyingAgent) && (
                            <span
                              title="Qualifying Agent"
                              className={[
                                'inline-flex h-7 w-7 items-center justify-center rounded-md',
                                screening.includeQualifyingAgent
                                  ? 'bg-[#007BFF]/15 text-[#007BFF]'
                                  : 'bg-gray-100 text-gray-400',
                              ].join(' ')}
                            >
                              <PhoneIcon className="h-4 w-4" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-1 rounded-md bg-gray-100 text-sm font-medium text-[#242124]">
                          {screening.candidateCount ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center hidden md:table-cell">
                        <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-1 rounded-md bg-gray-100 text-sm font-medium text-[#242124]">
                          {screening.shortlistedCount ?? 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell text-center">
                        <span className="text-sm text-[#242124]">
                          {screening.createdAt
                            ? new Date(screening.createdAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                            })
                            : '—'}
                        </span>
                      </td>
                      <td
                        className="px-6 py-4 text-right relative"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="relative action-menu-container">
                          <button
                            type="button"
                            ref={(el) => {
                              actionButtonRefs.current[screening._id] = el;
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openActionsDropdown === screening._id) {
                                setOpenActionsDropdown(null);
                                setActionMenuPosition(null);
                              } else {
                                const button = actionButtonRefs.current[screening._id];
                                if (button) {
                                  const rect = button.getBoundingClientRect();
                                  setActionMenuPosition({
                                    top: rect.top,
                                    right: window.innerWidth - rect.right,
                                  });
                                }
                                setOpenActionsDropdown(screening._id);
                              }
                            }}
                            className="text-gray-400 hover:text-gray-700 inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100 transition-colors"
                            title="Actions"
                          >
                            <EllipsisVerticalIcon className="h-5 w-5" />
                          </button>
                          {openActionsDropdown === screening._id &&
                            actionMenuPosition &&
                            createPortal(
                              <div
                                data-action-menu
                                className="fixed min-w-[180px] bg-white rounded-xl shadow-lg border border-gray-100 z-[9999] overflow-hidden"
                                style={{
                                  top: `${actionMenuPosition.top}px`,
                                  right: `${actionMenuPosition.right}px`,
                                  transform: 'translateY(calc(-100% - 8px))',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {/* Editing a qualifying screening was impossible until now: the
                                    row menu held only Delete, and every qualifyingAgent* field
                                    was write-once at creation. Fixing a question's wording meant
                                    deleting the round and losing its candidates with it. */}
                                {screening.includeQualifyingAgent && isQualifyingAgentEnabled && (
                                  <button
                                    type="button"
                                    data-testid="edit-qualifying-screening"
                                    onClick={() => {
                                      setQualifyingAgentEditing(screening);
                                      setShowQualifyingAgent(true);
                                      setOpenActionsDropdown(null);
                                      setActionMenuPosition(null);
                                    }}
                                    className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    <PencilSquareIcon className="h-4 w-4 mr-3 text-gray-400" />
                                    Edit questions &amp; details
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setScreeningPendingDelete(screening);
                                    setOpenActionsDropdown(null);
                                    setActionMenuPosition(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                                >
                                  <TrashIcon className="h-4 w-4 mr-3" />
                                  Delete screening
                                </button>
                              </div>,
                              document.body
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delete screening confirmation modal */}
        {screeningPendingDelete && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Delete screening</h2>
                <button
                  type="button"
                  onClick={() => setScreeningPendingDelete(null)}
                  className="inline-flex items-center justify-center rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                  aria-label="Close delete confirmation"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-3">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete the screening{' '}
                  <span className="font-semibold">
                    "{screeningPendingDelete.name || 'Untitled screening'}"
                  </span>
                  ?
                </p>
                <p className="text-sm text-gray-700">
                  This will permanently remove the screening and{' '}
                  <span className="font-semibold">
                    all associated candidates, interviews, assessments, and resume scores
                  </span>
                  .
                </p>
                <p className="text-sm font-semibold text-red-600">This action cannot be undone.</p>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setScreeningPendingDelete(null)}
                  className="inline-flex items-center rounded-lg px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                  disabled={!!deletingId}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteScreening(screeningPendingDelete)}
                  disabled={!!deletingId}
                  className="inline-flex items-center rounded-lg px-4 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deletingId ? 'Deleting...' : 'Delete screening'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Job description modal */}
        {showJobDescriptionModal && job && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 truncate">
                  {job.title || 'Job description'}
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleDownloadJobPdf}
                    className="inline-flex items-center justify-center rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                    aria-label="Download job description as PDF"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowJobDescriptionModal(false)}
                    className="inline-flex items-center justify-center rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                    aria-label="Close job description"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 flex-1 overflow-y-auto space-y-6">
                {/* Overview */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Overview
                  </h3>
                  <p className="text-sm text-gray-800 whitespace-pre-line">
                    {job.description || 'No description available for this job.'}
                  </p>
                </div>

                {/* Key details grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(Array.isArray(job.location) ? job.location.length > 0 : job.location) ||
                    job.locationType ? (
                    <div className="text-xs text-gray-500">
                      <div className="font-semibold uppercase tracking-wide mb-0.5">Location</div>
                      <div className="text-sm text-gray-800">
                        {formatLocationDisplay(job.location, job.locationType)}
                      </div>
                    </div>
                  ) : null}

                  {(job.experienceMin !== undefined || job.experienceMax !== undefined) && (
                    <div className="text-xs text-gray-500">
                      <div className="font-semibold uppercase tracking-wide mb-0.5">
                        Experience range
                      </div>
                      <div className="text-sm text-gray-800">
                        {job.experienceMin ?? '0'}–{job.experienceMax ?? '0'} years
                      </div>
                    </div>
                  )}

                  {(job.salaryMin !== undefined || job.salaryMax !== undefined) && (
                    <div className="text-xs text-gray-500">
                      <div className="font-semibold uppercase tracking-wide mb-0.5">Compensation</div>
                      <div className="text-sm text-gray-800">
                        {job.salaryCurrency || 'USD'} {job.salaryMin ?? 0}–{job.salaryMax ?? 0}
                      </div>
                    </div>
                  )}

                  {job.noticePeriod && (
                    <div className="text-xs text-gray-500">
                      <div className="font-semibold uppercase tracking-wide mb-0.5">
                        Notice Period
                      </div>
                      <div className="text-sm text-gray-800">{job.noticePeriod}</div>
                    </div>
                  )}

                  {(job.candidateQualification ||
                    job.genderPreference ||
                    job.ageMin !== undefined ||
                    job.ageMax !== undefined) && (
                      <div className="text-xs text-gray-500">
                        <div className="font-semibold uppercase tracking-wide mb-0.5">
                          Candidate profile
                        </div>
                        <div className="space-y-1 text-sm text-gray-800">
                          {job.candidateQualification && <div>{job.candidateQualification}</div>}
                          {(job.ageMin !== undefined || job.ageMax !== undefined) && (
                            <div>
                              Age: {job.ageMin ?? '—'}–{job.ageMax ?? '—'}
                            </div>
                          )}
                          {job.genderPreference && job.genderPreference !== 'any' && (
                            <div>Gender preference: {job.genderPreference}</div>
                          )}
                        </div>
                      </div>
                    )}
                </div>

                {/* Key responsibilities */}
                {job.keyResponsibilities && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Key responsibilities
                    </h3>
                    <p className="text-sm text-gray-800 whitespace-pre-line">
                      {job.keyResponsibilities}
                    </p>
                  </div>
                )}

                {/* Key attributes */}
                {job.keyAttributes && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Key attributes
                    </h3>
                    <p className="text-sm text-gray-800 whitespace-pre-line">{job.keyAttributes}</p>
                  </div>
                )}

                {/* Skills */}
                {Array.isArray(job.skills) && job.skills.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {job.skills.map((skill, idx) => (
                        <span
                          key={`${skill}-${idx}`}
                          className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}


      {/* Create screening modal */}
      {isCreateOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center transition-opacity duration-200 bg-black/40 backdrop-blur-[2px] px-4"
          onWheel={(e) => {
            // Prevent scroll on the overlay when modals are embedded
            if (hideCreateFlowEmbeddedChrome) {
              e.stopPropagation();
            }
          }}
          onTouchMove={(e) => {
            if (hideCreateFlowEmbeddedChrome) {
              e.stopPropagation();
            }
          }}
        >
          <div
            className="flex w-full transition-all duration-300 ease-out max-w-4xl h-[min(82dvh,820px)] max-h-[94dvh] rounded-2xl bg-white shadow-2xl overflow-hidden flex-col"
          >
            <form className="flex flex-1 flex-col min-w-0 overflow-hidden">
              {/* Top row: Steps + Close */}
              <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200 flex items-center justify-center gap-4 relative">
                <div className="flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => goToCreateStepFromStepper(1)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-none bg-none cursor-pointer text-sm font-medium transition-all duration-200 ${createStep === 1
                        ? 'bg-[#007BFF]/10 text-[#007BFF]'
                        : createStep > 1
                          ? 'text-gray-600 hover:bg-gray-50'
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                  >
                    <div
                      className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all ${createStep === 1
                          ? 'bg-[#007BFF] text-white'
                          : createStep > 1
                            ? 'bg-emerald-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                    >
                      {createStep > 1 ? <CheckIcon className="w-3 h-3" /> : <span>1</span>}
                    </div>
                    <span>Screening Details</span>
                  </button>

                  <span className="text-gray-300 mx-1 text-sm">→</span>

                  <button
                    type="button"
                    onClick={() => {
                      if (createStep >= 2) goToCreateStepFromStepper(2);
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-none bg-none cursor-pointer text-sm font-medium transition-all duration-200 ${createStep === 2
                        ? 'bg-[#007BFF]/10 text-[#007BFF]'
                        : createStep < 2
                          ? 'text-gray-500 hover:bg-gray-50'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    <div
                      className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all ${createStep === 2
                          ? 'bg-[#007BFF] text-white'
                          : createStep < 2
                            ? 'bg-gray-200 text-gray-500'
                            : 'bg-emerald-500 text-white'
                        }`}
                    >
                      {createStep > 2 ? <CheckIcon className="w-3 h-3" /> : <span>2</span>}
                    </div>
                    <span>Screening Methods</span>
                  </button>

                  {/* Resume Scoring Criteria step */}
                  {includeResumeScoring && (
                    <>
                      <span className="text-gray-300 mx-1 text-sm">→</span>
                      <button
                        type="button"
                        onClick={() => { if (createStep >= 3) goToCreateStepFromStepper(3); }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-none bg-none cursor-pointer text-sm font-medium transition-all duration-200 ${createStep === 3
                            ? 'bg-[#007BFF]/10 text-[#007BFF]'
                            : createStep < 3
                              ? 'text-gray-500 hover:bg-gray-50'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all ${createStep === 3
                            ? 'bg-[#007BFF] text-white'
                            : createStep < 3
                              ? 'bg-gray-200 text-gray-500'
                              : 'bg-emerald-500 text-white'
                          }`}>
                          {createStep > 3 ? <CheckIcon className="w-3 h-3" /> : <span>3</span>}
                        </div>
                        <span>Resume scoring</span>
                      </button>
                    </>
                  )}

                  {/* Document Collection step */}
                  {includeDocumentCollection && (
                    <>
                      <span className="text-gray-300 mx-1 text-sm">→</span>
                      <button
                        type="button"
                        onClick={() => { if (createStep >= 3 + rsOff) goToCreateStepFromStepper(3 + rsOff); }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-none bg-none cursor-pointer text-sm font-medium transition-all duration-200 ${createStep === 3 + rsOff
                            ? 'bg-[#007BFF]/10 text-[#007BFF]'
                            : createStep < 3 + rsOff
                              ? 'text-gray-500 hover:bg-gray-50'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all ${createStep === 3 + rsOff
                            ? 'bg-[#007BFF] text-white'
                            : createStep < 3 + rsOff
                              ? 'bg-gray-200 text-gray-500'
                              : 'bg-emerald-500 text-white'
                          }`}>
                          {createStep > 3 + rsOff ? <CheckIcon className="w-3 h-3" /> : <span>{3 + rsOff}</span>}
                        </div>
                        <span>Document setup</span>
                      </button>
                    </>
                  )}

                  {/* Assessment Setup step */}
                  {includeAssessment && (
                    <>
                      <span className="text-gray-300 mx-1 text-sm">→</span>
                      <button
                        type="button"
                        onClick={() => { if (createStep >= 3 + rsOff + dcOff) goToCreateStepFromStepper(3 + rsOff + dcOff); }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-none bg-none text-sm font-medium transition-all duration-200 ${createStep === 3 + rsOff + dcOff
                            ? 'bg-[#007BFF]/10 text-[#007BFF] cursor-pointer'
                            : createStep < 3 + rsOff + dcOff
                              ? 'text-gray-500 hover:bg-gray-50 cursor-pointer'
                              : 'text-gray-600 hover:bg-gray-50 cursor-pointer'
                          }`}
                      >
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all ${createStep === 3 + rsOff + dcOff
                            ? 'bg-[#007BFF] text-white'
                            : createStep < 3 + rsOff + dcOff
                              ? 'bg-gray-200 text-gray-500'
                              : 'bg-emerald-500 text-white'
                          }`}>
                          {createStep > 3 + rsOff + dcOff ? <CheckIcon className="w-3 h-3" /> : <span>{3 + rsOff + dcOff}</span>}
                        </div>
                        <span>Assessment setup</span>
                      </button>
                    </>
                  )}

                  {/* Interview Setup step */}
                  {includeInterview && (
                    <>
                      <span className="text-gray-300 mx-1 text-sm">→</span>
                      <button
                        type="button"
                        onClick={() => {
                          const targetStep = includeAssessment ? (4 + rsOff + dcOff) : (3 + rsOff + dcOff);
                          if (createStep >= targetStep && (assessmentConfigured || !includeAssessment)) {
                            setCreateStep(targetStep);
                          }
                        }}
                        disabled={includeAssessment && !assessmentConfigured}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-none bg-none text-sm font-medium transition-all duration-200 ${includeAssessment && !assessmentConfigured
                            ? 'text-gray-400 cursor-not-allowed'
                            : createStep === (includeAssessment ? (4 + rsOff + dcOff) : (3 + rsOff + dcOff))
                              ? 'bg-[#007BFF]/10 text-[#007BFF] cursor-pointer'
                              : createStep < (includeAssessment ? (4 + rsOff + dcOff) : (3 + rsOff + dcOff))
                                ? 'text-gray-500 hover:bg-gray-50 cursor-pointer'
                                : 'text-gray-600 hover:bg-gray-50 cursor-pointer'
                          }`}
                      >
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all ${includeAssessment && !assessmentConfigured
                            ? 'bg-gray-100 text-gray-400'
                            : createStep === (includeAssessment ? (4 + rsOff + dcOff) : (3 + rsOff + dcOff))
                              ? 'bg-[#007BFF] text-white'
                              : createStep < (includeAssessment ? (4 + rsOff + dcOff) : (3 + rsOff + dcOff))
                                ? 'bg-gray-200 text-gray-500'
                                : 'bg-emerald-500 text-white'
                          }`}>
                          {createStep > (includeAssessment ? (4 + rsOff + dcOff) : (3 + rsOff + dcOff)) ? <CheckIcon className="w-3 h-3" /> : <span>{includeAssessment ? (4 + rsOff + dcOff) : (3 + rsOff + dcOff)}</span>}
                        </div>
                        <span>Interview setup</span>
                      </button>
                    </>
                  )}

                  {/* Review step */}
                  <span className="text-gray-300 mx-1 text-sm">→</span>
                  <button
                    type="button"
                    onClick={() => { if (createStep >= reviewStep) setCreateStep(reviewStep); }}
                    disabled={
                      createStep < reviewStep ||
                      (includeAssessment && !assessmentConfigured) ||
                      (includeInterview && !interviewConfigured)
                    }
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-none bg-none text-sm font-medium transition-all duration-200 ${createStep === reviewStep
                        ? 'bg-[#007BFF]/10 text-[#007BFF] cursor-pointer'
                        : createStep < reviewStep
                          ? 'text-gray-500 hover:bg-gray-50 cursor-pointer'
                          : 'text-gray-600 hover:bg-gray-50 cursor-pointer'
                      }`}
                  >
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all ${createStep === reviewStep
                        ? 'bg-[#007BFF] text-white'
                        : createStep < reviewStep
                          ? 'bg-gray-200 text-gray-500'
                          : 'bg-emerald-500 text-white'
                      }`}>
                      {createStep > reviewStep ? <CheckIcon className="w-3 h-3" /> : <span>{reviewStep}</span>}
                    </div>
                    <span>Review</span>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleCancelCreateFlow}
                  className="absolute right-6 top-1/2 -translate-y-1/2 flex-shrink-0 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all border-none bg-none cursor-pointer"
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Header */}
              {!hideCreateFlowEmbeddedChrome &&
                createStep !== reviewStep && (
                  <div className="flex-shrink-0 px-6 py-5 border-b border-gray-200 bg-gray-50/50">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-semibold text-gray-900 mb-1">
                          {createStep === 3 && includeResumeScoring ? (
                            <span className="inline-flex items-center gap-2">
                              <SparklesIcon className="h-5 w-5 text-indigo-500" />
                              <span>Resume Scoring Criteria</span>
                              <span className="text-sm font-normal text-gray-400">
                                ({resumeScoringCriteria.length}/8)
                              </span>
                            </span>
                          ) : (
                            <>
                              {createStep === 1
                                ? 'Screening Details'
                                : createStep === 2
                                  ? 'Screening Methods'
                                  : createStep === 3 + rsOff && includeDocumentCollection
                                    ? 'Document Collection Setup'
                                    : createStep === 3 + rsOff + dcOff
                                      ? includeAssessment
                                        ? 'Choose Assessment Source'
                                        : 'Choose Interview Source'
                                        : createStep === 4 + rsOff + dcOff && includeInterview
                                          ? 'Interview Set up'
                                          : 'Set up each step you selected'}
                            </>
                          )}
                        </h2>
                        <p className="text-sm text-gray-600">
                          {createStep === 1
                            ? 'Let AI do the heavy lifting. Talentcrux screens candidates for you so you can focus on hiring the best. Name this round to begin.'
                            : createStep === 2
                              ? 'Select which screening methods to use for this round below; Talentcrux handles delivery, scoring, and analysis.'
                              : createStep === 3 && includeResumeScoring
                                ? 'Define the criteria AI will use to evaluate each resume. You can auto-generate from the JD or add your own.'
                                : createStep === 3 + rsOff && includeDocumentCollection
                                  ? 'Configure the list of documents you would like candidates to submit during their application.'
                                  : createStep === 3 + rsOff + dcOff
                                    ? includeAssessment
                                      ? 'Create a new assessment with AI assistance or select an existing one from your library.'
                                      : 'Choose how you want to set up this AI interview.'
                                      : createStep === 4 + rsOff + dcOff && includeInterview
                                        ? 'Set the categories and context for your AI interview.'
                                        : 'Configure the test and/or interview you selected. We’ll guide you through each one.'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleCancelCreateFlow}
                      className="absolute right-6 top-1/2 -translate-y-1/2 flex-shrink-0 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all border-none bg-none cursor-pointer"
                      aria-label="Close"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}



                  {/* Content Area */}
                  <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
                    <div
                      className={`flex-1 flex flex-col min-w-0 min-h-0 ${hideCreateFlowEmbeddedChrome
                        ? 'overflow-hidden'
                        : createStep === reviewStep
                          ? 'overflow-y-auto overscroll-contain scroll-smooth px-4 py-3 sm:px-6 sm:py-4'
                          : 'overflow-y-auto overscroll-contain scroll-smooth px-6 py-5'
                        }`}
                    >
                      {createStep === 1 && (
                        <div className="space-y-5 w-full">
                          <div className="w-full">
                            <label className="block text-sm font-medium text-gray-800 mb-1">
                              Screening name{' '}
                              <span className="text-red-500" aria-label="required">
                                *
                              </span>
                            </label>

                            <input
                              type="text"
                              required
                              value={draftName}
                              onChange={(e) => setDraftName(e.target.value)}
                              placeholder="Technical Assessment"
                              aria-describedby="name-hint"
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm transition-all duration-200 focus:outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/25 hover:border-gray-400"
                            />
                            <p id="name-hint" className="sr-only">
                              Choose a clear name based on what candidates will complete in this stage.
                            </p>
                          </div>

                          <div className="w-full">
                            <label className="block text-sm font-medium text-gray-800 mb-1">
                              Internal notes (optional)
                            </label>

                            <textarea
                              rows={2}
                              value={draftDescription}
                              onChange={(e) => setDraftDescription(e.target.value)}
                              placeholder="Add context or instructions for your hiring team. These notes are only visible internally and will not be shared with candidates."
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm transition-all duration-200 focus:outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/25 hover:border-gray-400 resize-y min-h-[80px]"
                            />
                          </div>
                        </div>
                      )}

                      {createStep === 2 && (
                        <div className="space-y-4 w-full">
                          <div>
                            <p className="text-sm font-medium text-gray-800 mb-2">
                              Select screening steps <span className="text-gray-500 font-normal">(choose one or more)</span>
                            </p>
                            <div className="space-y-2">
                              <div className={`rounded-lg border-2 transition-all duration-200 ${includeResumeScoring ? 'border-[#007BFF]/40 bg-[#007BFF]/5' : 'border-gray-200 bg-white hover:border-[#007BFF]/50 hover:bg-[#007BFF]/5'}`}>
                                <label className="flex cursor-pointer items-start gap-3 px-4 py-3.5 focus-within:ring-2 focus-within:ring-[#007BFF]/20">
                                  <input
                                    type="checkbox"
                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]"
                                    checked={includeResumeScoring}
                                    onChange={(e) => {
                                      const nextChecked = e.target.checked;
                                      if (!nextChecked) {
                                        const hasResumeData =
                                          Array.isArray(resumeScoringCriteria) && resumeScoringCriteria.length > 0;
                                        if (hasResumeData) {
                                          requestRemoveMethod('resume', {
                                            title: 'Remove Resume Scoring?',
                                            message:
                                              'This will clear your resume scoring criteria for this screening setup.',
                                            onConfirm: async () => {
                                              setIncludeResumeScoring(false);
                                              setResumeScoringCriteria([]);
                                              setResumeCriteriaCreateError('');
                                              setResumeCriteriaCreating(false);
                                            },
                                          });
                                          return;
                                        }
                                        setIncludeResumeScoring(false);
                                        setResumeScoringCriteria([]);
                                        setResumeCriteriaCreateError('');
                                        setResumeCriteriaCreating(false);
                                        return;
                                      }
                                      setIncludeResumeScoring(true);
                                    }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-gray-900">
                                      Resume Scoring
                                    </div>
                                    <p className="text-xs text-gray-600 mt-0.5">
                                      AI scores candidates’ resumes for this job and ranks them so you can focus on the best matches.
                                    </p>
                                    {includeResumeScoring && (
                                      <div className="mt-2">
                                        {resumeCriteriaCreating ? (
                                          <div className="flex items-center gap-2 text-xs text-gray-600">
                                            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#007BFF]/60 border-t-transparent" />
                                            Generating criteria…
                                          </div>
                                        ) : resumeScoringCriteria.length > 0 ? (
                                          <div className="text-xs text-gray-600">
                                            Generated <span className="font-semibold">{resumeScoringCriteria.length}</span> criteria for this job.
                                          </div>
                                        ) : resumeCriteriaCreateError ? (
                                          <div className="flex items-center gap-2 text-xs text-red-600">
                                            <span className="flex-1 min-w-0">{resumeCriteriaCreateError}</span>
                                          </div>
                                        ) : (
                                          <div className="text-xs text-gray-600">
                                            Criteria will be generated after you click <span className="font-semibold">Next</span>.
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </label>
                              </div>

                              <label className="flex items-start gap-3 rounded-lg border-2 px-4 py-3.5 transition-all duration-200 focus-within:ring-2 focus-within:ring-[#007BFF]/20 focus-within:border-[#007BFF] cursor-pointer border-gray-200 bg-white hover:border-[#007BFF]/50 hover:bg-[#007BFF]/5">
                                <input
                                  type="checkbox"
                                  className="mt-1 h-4 w-4 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]"
                                  checked={includeAssessment}
                                  onChange={(e) => {
                                    const nextChecked = e.target.checked;
                                    if (!nextChecked) {
                                      const hasAssessmentData = Boolean(
                                        configuredAssessmentId || assessmentConfigured || assessmentSource
                                      );
                                      if (hasAssessmentData) {
                                        requestRemoveMethod('assessment', {
                                          title: 'Remove Online Assessment?',
                                          message:
                                            'This will delete the assessment you created for this screening setup.',
                                          onConfirm: async () => {
                                            await discardConfiguredAssessment();
                                            setIncludeAssessment(false);
                                          },
                                        });
                                        return;
                                      }
                                      setIncludeAssessment(false);
                                      return;
                                    }
                                    setIncludeAssessment(true);
                                  }}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-900">
                                    Online Assessment
                                  </div>
                                  <p className="text-xs text-gray-600 mt-0.5">
                                    Skill-based assessments that surface candidates' real knowledge. Talentcrux delivers the test and scores it so you get clear insights into who actually has the skills.
                                  </p>
                                </div>
                              </label>

                              {isApplicationFormEnabled && (
                                <label className="flex items-start gap-3 rounded-lg border-2 px-4 py-3.5 transition-all duration-200 focus-within:ring-2 focus-within:ring-[#007BFF]/20 focus-within:border-[#007BFF] cursor-pointer border-gray-200 bg-white hover:border-[#007BFF]/50 hover:bg-[#007BFF]/5">
                                  <input
                                    type="checkbox"
                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]"
                                    checked={includeApplicationForm}
                                    onChange={(e) => setIncludeApplicationForm(e.target.checked)}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-gray-900">
                                      Application Form
                                    </div>
                                    <p className="text-xs text-gray-600 mt-0.5">
                                      Candidates fill the pre-interview application form (personal, family, education, experience and remuneration details) after their resume and documents, prefilled from their resume. You can view it and download it as a PDF.
                                    </p>
                                  </div>
                                </label>
                              )}

                              <label className="flex items-start gap-3 rounded-lg border-2 px-4 py-3.5 transition-all duration-200 focus-within:ring-2 focus-within:ring-[#007BFF]/20 focus-within:border-[#007BFF] cursor-pointer border-gray-200 bg-white hover:border-[#007BFF]/50 hover:bg-[#007BFF]/5">
                                <input
                                  type="checkbox"
                                  className="mt-1 h-4 w-4 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]"
                                  checked={includeInterview}
                                  onChange={(e) => {
                                    const nextChecked = e.target.checked;
                                    if (!nextChecked) {
                                      const hasInterviewData = Boolean(
                                        configuredInterviewId || interviewConfigured || interviewSource
                                      );
                                      if (hasInterviewData) {
                                        requestRemoveMethod('interview', {
                                          title: 'Remove AI Interview?',
                                          message:
                                            'This will delete the AI interview you created for this screening setup.',
                                          onConfirm: async () => {
                                            await discardConfiguredInterview();
                                            setIncludeInterview(false);
                                          },
                                        });
                                        return;
                                      }
                                      setIncludeInterview(false);
                                      return;
                                    }
                                    setIncludeInterview(true);
                                  }}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-900">
                                    AI Interview
                                  </div>
                                  <p className="text-xs text-gray-600 mt-0.5">
                                    Let AI run the interview and surface the best candidates. You get evaluations and scorecards for comparison—so you spend less time on calls and more on hiring the right fit.
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 rounded-lg border-2 px-4 py-3.5 transition-all duration-200 focus-within:ring-2 focus-within:ring-[#007BFF]/20 focus-within:border-[#007BFF] cursor-pointer border-gray-200 bg-white hover:border-[#007BFF]/50 hover:bg-[#007BFF]/5">
                                <input
                                  type="checkbox"
                                  className="mt-1 h-4 w-4 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]"
                                  checked={includeDocumentCollection}
                                  onChange={(e) => {
                                    const nextChecked = e.target.checked;
                                    if (!nextChecked) {
                                      const hasDcData = Array.isArray(requestedDocuments) && requestedDocuments.length > 0;
                                      if (hasDcData) {
                                        requestRemoveMethod('documentCollection', {
                                          title: 'Remove Document Collection?',
                                          message: 'This will clear all configured documents to collect for this screening setup.',
                                          onConfirm: async () => {
                                            setIncludeDocumentCollection(false);
                                            setRequestedDocuments([]);
                                          },
                                        });
                                        return;
                                      }
                                      setIncludeDocumentCollection(false);
                                      return;
                                    }
                                    setIncludeDocumentCollection(true);
                                  }}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-900">
                                    Document Collection
                                  </div>
                                  <p className="text-xs text-gray-600 mt-0.5">
                                    Collect candidate background verification (BGV) documents (e.g. marksheets, previous offer letters) securely during their application.
                                  </p>
                                </div>
                              </label>
                            </div>
                            <p className="text-xs text-gray-600 mt-2">
                              Select at least one method to continue. You can choose multiple if required.
                            </p>
                          </div>

                          {/* Qualification Gating (Journey Automation) */}
                          <div className="mt-6 border-t border-gray-200 pt-5">
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-900">Qualification Gating (Journey Automation)</h4>
                                  <p className="text-xs text-gray-600 mt-0.5">
                                    Automatically reject candidates who do not meet the minimum score criteria at each stage.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={journeyAutomationEnabled}
                                  onClick={() => setJourneyAutomationEnabled(!journeyAutomationEnabled)}
                                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007BFF] ${!journeyAutomationEnabled ? 'bg-gray-200' : ''}`}
                                  style={journeyAutomationEnabled ? { backgroundColor: '#007BFF' } : undefined}
                                >
                                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${journeyAutomationEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                                </button>
                              </div>
                              {journeyAutomationEnabled && (
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-gray-200/60 pt-4">
                                  {includeResumeScoring && (
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                                        Minimum Resume Score (%)
                                      </label>
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={journeyAutomationResumeMinScore}
                                        onChange={(e) => setJourneyAutomationResumeMinScore(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF] outline-none"
                                      />
                                    </div>
                                  )}
                                  {includeAssessment && (
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                                        Minimum Assessment Score (%)
                                      </label>
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={journeyAutomationAssessmentMinScore}
                                        onChange={(e) => setJourneyAutomationAssessmentMinScore(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF] outline-none"
                                      />
                                    </div>
                                  )}
                                  {!includeResumeScoring && !includeAssessment && (
                                    <p className="text-xs text-amber-600 font-medium col-span-2">
                                      Please enable Resume Scoring or Online Assessment to apply gating thresholds.
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* ARM Salary Request (Go Colors only) */}
                          {armSalaryDetailsEnabled && (
                            <div className="mt-4 border-t border-gray-200 pt-4">
                              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h4 className="text-sm font-semibold text-gray-900">ARM Salary Details Request</h4>
                                    <p className="text-xs text-gray-600 mt-0.5">
                                      When a candidate is moved to Selected, automatically email the store ARM to fill in salary &amp; joining date.
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={enableArmSalaryRequest}
                                    onClick={() => setEnableArmSalaryRequest(!enableArmSalaryRequest)}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 ${!enableArmSalaryRequest ? 'bg-gray-200' : ''}`}
                                    style={enableArmSalaryRequest ? { backgroundColor: '#10b981' } : undefined}
                                  >
                                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${enableArmSalaryRequest ? 'translate-x-5' : 'translate-x-0'}`} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                  {/* Step 3: Resume Scoring Criteria (when resume scoring is selected) */}
                  {createStep === 3 && includeResumeScoring && (
                    <div className="w-full">
                      <ResumeScoreConfig
                        jobId={jobId}
                        criteria={resumeScoringCriteria}
                        onCriteriaChange={setResumeScoringCriteria}
                      />
                    </div>
                  )}

                  {/* Step 3 + rsOff: Document Collection Setup */}
                  {createStep === 3 + rsOff && includeDocumentCollection && (
                    <div className="w-full">
                      <DocumentCollectionConfig
                        requestedDocuments={requestedDocuments}
                        onChange={setRequestedDocuments}
                        enableVerification={enableDocumentVerification}
                        onVerificationChange={setEnableDocumentVerification}
                      />
                    </div>
                  )}


                  {/* Assessment setup - embedded modal */}
                  {createStep === 3 + rsOff + dcOff && showAssessmentModal && includeAssessment && jobId && (
                    <div
                      className="flex-1 flex flex-col min-w-0 min-h-0"
                      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                      onWheel={(e) => e.stopPropagation()}
                      onTouchMove={(e) => e.stopPropagation()}
                    >
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                        <AssessmentCreateFlowModal
                          jobPostingId={jobId}
                          screeningName={draftName?.trim() || ''}
                          skipInviteStep
                          hasNextStep={includeInterview && !interviewConfigured}
                          onClose={handleAssessmentComplete}
                          isLibraryFlow={assessmentSource === 'library'}
                        />
                      </div>
                    </div>
                  )}

                  {/* Interview setup flow when only Interview is selected */}
                  {createStep === 3 + rsOff + dcOff && includeInterview && !includeAssessment && !showInterviewModal && !showInterviewLibraryPicker && (
                    <div className="max-w-2xl mx-auto space-y-4">
                      <div className="space-y-3">
                        {/* Option 1: Create Interview with AI */}
                        <button
                          type="button"
                          onClick={() => setInterviewSource('ai')}
                          className={`w-full flex items-start gap-4 p-5 rounded-xl border-2 transition-all duration-200 text-left ${interviewSource === 'ai'
                              ? 'border-[#007BFF] bg-[#007BFF]/5 ring-2 ring-[#007BFF]/20'
                              : 'border-gray-200 hover:border-[#007BFF]/50 hover:bg-gray-50'
                            }`}
                        >
                          <div
                            className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${interviewSource === 'ai' ? 'bg-[#007BFF]/15' : 'bg-gray-100'
                              }`}
                          >
                            <ChatBubbleLeftRightIcon
                              className={`w-6 h-6 ${interviewSource === 'ai' ? 'text-[#007BFF]' : 'text-gray-500'
                                }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-base font-semibold ${interviewSource === 'ai' ? 'text-[#007BFF]' : 'text-gray-900'
                                  }`}
                              >
                                Create interview with AI
                              </span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#007BFF]/10 text-[#007BFF]">
                                Recommended
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              Let our AI generate an interview tailored to this screening. Talentcrux
                              will handle delivery, proctoring, and evaluation.
                            </p>
                          </div>
                          <div
                            className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${interviewSource === 'ai'
                                ? 'border-[#007BFF] bg-[#007BFF]'
                                : 'border-gray-300'
                              }`}
                          >
                            {interviewSource === 'ai' && <CheckIcon className="w-3 h-3 text-white" />}
                          </div>
                        </button>

                        {/* Option 2: Choose from Library */}
                        <button
                          type="button"
                          onClick={() => setInterviewSource('library')}
                          className={`w-full flex items-start gap-4 p-5 rounded-xl border-2 transition-all duration-200 text-left ${interviewSource === 'library'
                              ? 'border-[#007BFF] bg-[#007BFF]/5 ring-2 ring-[#007BFF]/20'
                              : 'border-gray-200 hover:border-[#007BFF]/50 hover:bg-gray-50'
                            }`}
                        >
                          <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-gray-100">
                            <ClipboardDocumentListIcon className={`w-6 h-6 ${interviewSource === 'library' ? 'text-[#007BFF]' : 'text-gray-500'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className={`text-base font-semibold ${interviewSource === 'library' ? 'text-[#007BFF]' : 'text-gray-900'}`}>
                              Choose from Library
                            </span>
                            <p className="text-sm text-gray-600 mt-1">
                              Select a saved AI interview template from your library and reuse it in this screening.
                            </p>
                          </div>
                          <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${interviewSource === 'library' ? 'border-[#007BFF] bg-[#007BFF]' : 'border-gray-300'
                            }`}>
                            {interviewSource === 'library' && <CheckIcon className="w-3 h-3 text-white" />}
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                      {createStep === 3 + rsOff + dcOff &&
                        showInterviewLibraryPicker &&
                        includeInterview &&
                        !includeAssessment &&
                        jobId && (
                          <div
                            className="flex-1 flex flex-col min-w-0 min-h-0"
                            style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', minHeight: 0 }}
                          >
                            <div
                              className="flex-1 flex flex-col min-h-0 overflow-hidden"
                              style={{ minHeight: 0, height: '100%' }}
                            >
                              <AIInterviewLibraryPickerModal
                                open={showInterviewLibraryPicker}
                                embedded
                                onClose={() => setShowInterviewLibraryPicker(false)}
                                onPick={handlePickInterviewTemplate}
                              />
                            </div>
                          </div>
                        )}

                      {createStep === 3 + rsOff + dcOff &&
                        showInterviewModal &&
                        includeInterview &&
                        !includeAssessment &&
                        jobId && (
                          <div
                            className="flex-1 flex flex-col min-w-0 min-h-0"
                            style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}
                            onWheel={(e) => e.stopPropagation()}
                          >
                            <div
                              className="flex-1 flex flex-col min-h-0 overflow-hidden"
                              style={{ minHeight: 0, height: '100%' }}
                            >
                              <Suspense
                                fallback={
                                  <div className="flex items-center justify-center h-full">
                                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-600" />
                                  </div>
                                }
                              >
                                <div
                                  style={{
                                    flex: '1 1 0%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: 0,
                                    height: '100%',
                                    maxHeight: '100%',
                                    width: '100%',
                                  }}
                                >
                                  <AIInterviewCreate
                                    isModal
                                    skipInviteStep
                                    jobPostingId={jobId}
                                    screeningName={draftName?.trim() || ''}
                                    onClose={handleInterviewComplete}
                                  />
                                </div>
                              </Suspense>
                            </div>
                          </div>
                        )}
 
                      {/* Assessment Source Selection */}
                      {createStep === 3 + rsOff + dcOff && includeAssessment && !showAssessmentModal && (
                        <div className="max-w-2xl mx-auto space-y-4">
                          <div className="space-y-3">
                            {/* Option 1: Create with AI */}
                            <button
                              type="button"
                              onClick={() => setAssessmentSource('ai')}
                              className={`w-full flex items-start gap-4 p-5 rounded-xl border-2 transition-all duration-200 text-left ${assessmentSource === 'ai'
                                ? 'border-[#007BFF] bg-[#007BFF]/5 ring-2 ring-[#007BFF]/20'
                                : 'border-gray-200 hover:border-[#007BFF]/50 hover:bg-gray-50'
                                }`}
                            >
                              <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${assessmentSource === 'ai' ? 'bg-[#007BFF]/15' : 'bg-gray-100'
                                }`}>
                                <SparklesIcon className={`w-6 h-6 ${assessmentSource === 'ai' ? 'text-[#007BFF]' : 'text-gray-500'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`text-base font-semibold ${assessmentSource === 'ai' ? 'text-[#007BFF]' : 'text-gray-900'}`}>
                                    Create with AI
                                  </span>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#007BFF]/10 text-[#007BFF]">
                                    Recommended
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                  Let our AI generate relevant assessment questions based on the job requirements. Fast, accurate, and tailored to your needs.
                                </p>
                              </div>
                              <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${assessmentSource === 'ai' ? 'border-[#007BFF] bg-[#007BFF]' : 'border-gray-300'
                                }`}>
                                {assessmentSource === 'ai' && <CheckIcon className="w-3 h-3 text-white" />}
                              </div>
                            </button>

                            {/* Option 2: Choose from Library */}
                            <button
                              type="button"
                              onClick={() => setAssessmentSource('library')}
                              className={`w-full flex items-start gap-4 p-5 rounded-xl border-2 transition-all duration-200 text-left ${assessmentSource === 'library'
                                ? 'border-[#007BFF] bg-[#007BFF]/5 ring-2 ring-[#007BFF]/20'
                                : 'border-gray-200 hover:border-[#007BFF]/50 hover:bg-gray-50'
                                }`}
                            >
                              <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${assessmentSource === 'library' ? 'bg-[#007BFF]/15' : 'bg-gray-100'
                                }`}>
                                <ClipboardDocumentListIcon className={`w-6 h-6 ${assessmentSource === 'library' ? 'text-[#007BFF]' : 'text-gray-500'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className={`text-base font-semibold ${assessmentSource === 'library' ? 'text-[#007BFF]' : 'text-gray-900'}`}>
                                  Choose from Library
                                </span>
                                <p className="text-sm text-gray-600 mt-1">
                                  Select an existing assessment from your organization's library. Reuse proven assessments you've created before.
                                </p>
                              </div>
                              <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${assessmentSource === 'library' ? 'border-[#007BFF] bg-[#007BFF]' : 'border-gray-300'
                                }`}>
                                {assessmentSource === 'library' && <CheckIcon className="w-3 h-3 text-white" />}
                              </div>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Interview Set up (interview modal or landing) */}
                      {createStep === 4 + rsOff + dcOff && includeInterview && !interviewConfigured && (
                        <>
                          {/* When Assessment + Interview are selected and Assessment is already configured,
                          first ask user to choose how to set up the Interview */}
                          {includeAssessment && assessmentConfigured && !showInterviewModal && !showInterviewLibraryPicker ? (
                            <div className="max-w-2xl mx-auto space-y-4">
                              <div className="space-y-3">
                                {/* Option 1: Create Interview with AI */}
                                <button
                                  type="button"
                                  onClick={() => setInterviewSource('ai')}
                                  className={`w-full flex items-start gap-4 p-5 rounded-xl border-2 transition-all duration-200 text-left ${interviewSource === 'ai'
                                    ? 'border-[#007BFF] bg-[#007BFF]/5 ring-2 ring-[#007BFF]/20'
                                    : 'border-gray-200 hover:border-[#007BFF]/50 hover:bg-gray-50'
                                    }`}
                                >
                                  <div
                                    className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${interviewSource === 'ai' ? 'bg-[#007BFF]/15' : 'bg-gray-100'
                                      }`}
                                  >
                                    <ChatBubbleLeftRightIcon
                                      className={`w-6 h-6 ${interviewSource === 'ai' ? 'text-[#007BFF]' : 'text-gray-500'
                                        }`}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span
                                        className={`text-base font-semibold ${interviewSource === 'ai' ? 'text-[#007BFF]' : 'text-gray-900'
                                          }`}
                                      >
                                        Create interview with AI
                                      </span>
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#007BFF]/10 text-[#007BFF]">
                                        Recommended
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">
                                      Let our AI generate an interview tailored to this screening. Talentcrux
                                      will handle delivery, proctoring, and evaluation.
                                    </p>
                                  </div>
                                  <div
                                    className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${interviewSource === 'ai'
                                      ? 'border-[#007BFF] bg-[#007BFF]'
                                      : 'border-gray-300'
                                      }`}
                                  >
                                    {interviewSource === 'ai' && (
                                      <CheckIcon className="w-3 h-3 text-white" />
                                    )}
                                  </div>
                                </button>

                                {/* Option 2: Choose from Library */}
                                <button
                                  type="button"
                                  onClick={() => setInterviewSource('library')}
                                  className={`w-full flex items-start gap-4 p-5 rounded-xl border-2 transition-all duration-200 text-left ${interviewSource === 'library'
                                    ? 'border-[#007BFF] bg-[#007BFF]/5 ring-2 ring-[#007BFF]/20'
                                    : 'border-gray-200 hover:border-[#007BFF]/50 hover:bg-gray-50'
                                    }`}
                                >
                                  <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-gray-100">
                                    <ClipboardDocumentListIcon className={`w-6 h-6 ${interviewSource === 'library' ? 'text-[#007BFF]' : 'text-gray-500'}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className={`text-base font-semibold ${interviewSource === 'library' ? 'text-[#007BFF]' : 'text-gray-900'}`}>
                                      Choose from Library
                                    </span>
                                    <p className="text-sm text-gray-600 mt-1">
                                      Select a saved AI interview template from your library and reuse it in this screening.
                                    </p>
                                  </div>
                                  <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${interviewSource === 'library' ? 'border-[#007BFF] bg-[#007BFF]' : 'border-gray-300'
                                    }`}>
                                    {interviewSource === 'library' && <CheckIcon className="w-3 h-3 text-white" />}
                                  </div>
                                </button>
                              </div>
                            </div>
                          ) : showInterviewLibraryPicker && jobId && (assessmentConfigured || !includeAssessment) ? (
                            <div
                              className="flex-1 flex flex-col min-w-0 min-h-0 relative"
                              style={{ height: '100%', maxHeight: '100%', minHeight: 0 }}
                            >
                              <div
                                className="flex-1 flex flex-col min-w-0 min-h-0"
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  overflow: 'hidden',
                                  height: '100%',
                                  maxHeight: '100%',
                                  flex: '1 1 0%',
                                }}
                              >
                                <div
                                  className="flex-1 flex flex-col min-h-0 overflow-hidden"
                                  style={{ minHeight: 0, height: '100%' }}
                                >
                                  <AIInterviewLibraryPickerModal
                                    open={showInterviewLibraryPicker}
                                    embedded
                                    onClose={() => setShowInterviewLibraryPicker(false)}
                                    onPick={handlePickInterviewTemplate}
                                  />
                                </div>
                              </div>
                            </div>
                          ) : showInterviewModal && jobId && (assessmentConfigured || !includeAssessment) ? (
                            <div
                              className="flex-1 flex flex-col min-w-0 min-h-0 relative"
                              style={{ height: '100%', maxHeight: '100%' }}
                            >
                              <div
                                className="flex-1 flex flex-col min-w-0 min-h-0"
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  overflow: 'hidden',
                                  height: '100%',
                                  maxHeight: '100%',
                                  flex: '1 1 0%',
                                }}
                                onWheel={(e) => e.stopPropagation()}
                              >
                                <div
                                  className="flex-1 flex flex-col min-h-0 overflow-hidden"
                                  style={{ minHeight: 0, height: '100%' }}
                                >
                                  <Suspense
                                    fallback={
                                      <div className="flex items-center justify-center h-full">
                                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-600" />
                                      </div>
                                    }
                                  >
                                    <div
                                      style={{
                                        flex: '1 1 0%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        minHeight: 0,
                                        height: '100%',
                                        maxHeight: '100%',
                                        width: '100%',
                                      }}
                                    >
                                      <AIInterviewCreate
                                        isModal
                                        skipInviteStep
                                        jobPostingId={jobId}
                                        screeningName={draftName?.trim() || ''}
                                        onClose={handleInterviewComplete}
                                      />
                                    </div>
                                  </Suspense>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="max-w-2xl mx-auto space-y-4">
                              {/* Progress indicators */}
                              <div className="space-y-2">
                                {includeAssessment && (
                                  <div className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50">
                                    {assessmentConfigured ? (
                                      <>
                                        <CheckIcon className="h-5 w-5 text-emerald-600" />
                                        <span className="text-sm font-medium text-gray-900">
                                          Assessment configured
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <div className="h-5 w-5 rounded-full border-2 border-gray-300 border-t-indigo-600 animate-spin" />
                                        <span className="text-sm text-gray-600">
                                          Configuring Assessment...
                                        </span>
                                      </>
                                    )}
                                  </div>
                                )}

                                {includeInterview && (
                                  <div className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50">
                                    {interviewConfigured ? (
                                      <>
                                        <CheckIcon className="h-5 w-5 text-emerald-600" />
                                        <span className="text-sm font-medium text-gray-900">
                                          Interview configured
                                        </span>
                                      </>
                                    ) : assessmentConfigured || !includeAssessment ? (
                                      <>
                                        <div className="h-5 w-5 rounded-full border-2 border-gray-300 border-t-indigo-600 animate-spin" />
                                        <span className="text-sm text-gray-600">
                                          Configuring Interview...
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                                        <span className="text-sm text-gray-500">
                                          Waiting for Assessment to complete...
                                        </span>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Instructions when no modals are showing */}
                              <div className="text-center py-8 text-gray-500">
                                {includeAssessment && !assessmentConfigured ? (
                                  <p className="text-sm">
                                    Click &quot;Configure Assessment&quot; below to set up your
                                    assessment.
                                  </p>
                                ) : includeInterview && !interviewConfigured ? (
                                  <p className="text-sm">
                                    Click &quot;Configure Interview&quot; below to set up your
                                    interview.
                                  </p>
                                ) : (
                                  <p className="text-sm">
                                    All selected components have been configured. You can proceed to
                                    create the screening.
                                  </p>
                                )}
                              </div>

                              {/* Manual trigger buttons if modals are closed (Step 4 = Interview only; Assessment is on Step 3) */}
                              <div className="flex gap-2">
                                {includeInterview &&
                                  !interviewConfigured &&
                                  (assessmentConfigured || !includeAssessment) && (
                                    <button
                                      type="button"
                                      onClick={() => setShowInterviewModal(true)}
                                      className="px-4 py-2 rounded-xl bg-[#0056b3] text-sm font-medium text-white hover:bg-[#004494] transition-colors"
                                    >
                                      Configure Interview
                                    </button>
                                  )}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Review step */}
                      {createStep === reviewStep && (
                        <div className="flex flex-col min-h-0 w-full max-w-4xl mx-auto">
                          {/* Hero */}
                          <div className="text-center mb-3 sm:mb-4 flex-shrink-0">
                            <div className="mx-auto w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-emerald-50 flex items-center justify-center mb-2">
                              <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
                            </div>
                            <h2 className="text-base sm:text-lg font-semibold text-gray-900 px-1">Your Screening is Ready!</h2>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1 px-2 max-w-md mx-auto leading-snug">
                              Review below, then click Finish to create this screening.
                            </p>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 min-h-0 items-stretch">
                            {/* Left: Screening Details + Components combined */}
                            <div className="rounded-xl border border-gray-200/80 bg-white p-3 sm:p-4 hover:border-[#007BFF]/20 transition-colors flex flex-col shadow-sm">
                              {/* Screening Details */}
                              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100 flex-shrink-0">
                                <div className="w-8 h-8 rounded-lg bg-[#007BFF]/10 flex items-center justify-center">
                                  <ClipboardDocumentListIcon className="h-4 w-4 text-[#007BFF]" />
                                </div>
                                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                  SCREENING DETAILS
                                </span>
                              </div>
                              <div className="mb-3">
                                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                                  <p className="text-lg sm:text-xl font-semibold text-gray-900 break-words min-w-0">{draftName || '—'}</p>
                                  <span className="px-3 py-1 rounded-full bg-gray-100 text-sm text-gray-700 shrink-0">
                                    Duration: {5 + (includeAssessment && assessmentConfigured ? (configuredAssessmentDuration ?? 0) : 0) + (includeInterview ? 10 : 0)} min
                                  </span>
                                </div>
                                {draftDescription ? (
                                  <p className="text-sm text-gray-700 mb-3">{draftDescription}</p>
                                ) : (
                                  <p className="text-sm text-gray-400 italic mb-3">No description</p>
                                )}
                              </div>

                              {/* AI Components */}
                              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100 flex-shrink-0">
                                <div className="w-8 h-8 rounded-lg bg-[#007BFF]/10 flex items-center justify-center">
                                  <PuzzlePieceIcon className="h-4 w-4 text-[#007BFF]" />
                                </div>
                                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">COMPONENTS</span>
                              </div>
                              <div className="space-y-2 py-1">
                                {includeResumeScoring && (
                                  <div className="p-2 rounded-lg bg-[#F0F7FF] border border-[#DDEEFF]">
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="flex items-center gap-3">
                                        <SparklesIcon className="h-5 w-5 text-[#007BFF]" />
                                        <div className="text-sm font-semibold text-[#007BFF]">AI Resume Scoring</div>
                                      </div>
                                      {resumeScoringCriteria.length > 0 && (
                                        <span className="text-[10px] font-medium text-[#007BFF]/70 bg-white px-2 py-0.5 rounded-full border border-[#DDEEFF]">
                                          {resumeScoringCriteria.length} criteria
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {includeAssessment && assessmentConfigured && (
                                  <div className="p-2 rounded-lg bg-[#F0F7FF] border border-[#DDEEFF]">
                                    <div className="flex items-center gap-3">
                                      <DocumentTextIcon className="h-5 w-5 text-[#007BFF]" />
                                      <div className="text-sm font-semibold text-[#007BFF]">Skill-based Assessment</div>
                                    </div>
                                  </div>
                                )}
                                {includeDocumentCollection && (
                                  <div className="p-2 rounded-lg bg-[#F0F7FF] border border-[#DDEEFF]">
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="flex items-center gap-3">
                                        <DocumentTextIcon className="h-5 w-5 text-[#007BFF]" />
                                        <div className="text-sm font-semibold text-[#007BFF]">BGV Document Collection</div>
                                      </div>
                                      {requestedDocuments.length > 0 && (
                                        <span className="text-[10px] font-medium text-[#007BFF]/70 bg-white px-2 py-0.5 rounded-full border border-[#DDEEFF]">
                                          {requestedDocuments.length} docs
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {isApplicationFormEnabled && includeApplicationForm && (
                                  <div className="p-2 rounded-lg bg-[#F0F7FF] border border-[#DDEEFF]">
                                    <div className="flex items-center gap-3">
                                      <DocumentTextIcon className="h-5 w-5 text-[#007BFF]" />
                                      <div className="text-sm font-semibold text-[#007BFF]">Application Form</div>
                                    </div>
                                  </div>
                                )}
                                {includeInterview && interviewConfigured && (
                                  <div className="p-2 rounded-lg bg-[#F0F7FF] border border-[#DDEEFF]">
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="flex items-center gap-3">
                                        <ChatBubbleLeftRightIcon className="h-5 w-5 text-[#007BFF]" />
                                        <div className="text-sm font-semibold text-[#007BFF]">AI Interview</div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={handleResetInterviewConfiguration}
                                        className="text-xs font-medium text-[#007BFF] hover:text-[#0056B3] border-none bg-transparent cursor-pointer underline-offset-2 hover:underline"
                                      >
                                        Change
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Right: What happens next */}
                            <div className="rounded-xl border border-[#DDEEFF] bg-gradient-to-br from-white to-blue-50/90 p-3 sm:p-4 flex flex-col shadow-sm">
                              <h3 className="text-xs sm:text-sm font-semibold text-gray-800 mb-2 sm:mb-3 flex items-center gap-2 flex-shrink-0">
                                <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#007BFF] shrink-0" />
                                What happens next?
                              </h3>
                              <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
                                <div className="flex items-start gap-2.5 sm:gap-3">
                                  <div className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#007BFF] text-white text-[10px] sm:text-xs font-medium flex-shrink-0">1</div>
                                  <div className="min-w-0">
                                    <div className="text-xs sm:text-sm font-semibold text-gray-800">Screening created</div>
                                    <p className="text-[11px] sm:text-xs text-gray-600 mt-0.5 leading-snug">
                                      You&apos;ll open the detail page to manage candidates and view results.
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2.5 sm:gap-3">
                                  <div className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#007BFF] text-white text-[10px] sm:text-xs font-medium flex-shrink-0">2</div>
                                  <div className="min-w-0">
                                    <div className="text-xs sm:text-sm font-semibold text-gray-800">Invite candidates</div>
                                    <p className="text-[11px] sm:text-xs text-gray-600 mt-0.5 leading-snug">
                                      Share a link, email, or bulk upload—invitees complete the steps you turned on.
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2.5 sm:gap-3">
                                  <div className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#007BFF] text-white text-[10px] sm:text-xs font-medium flex-shrink-0">3</div>
                                  <div className="min-w-0">
                                    <div className="text-xs sm:text-sm font-semibold text-gray-800">AI analysis and select</div>
                                    <p className="text-[11px] sm:text-xs text-gray-600 mt-0.5 leading-snug">
                                      Resume, assessment, and interview signals are scored so you can select in one place.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer — hidden when embedded assessment/interview flow is actually visible */}
                    {!hideCreateFlowEmbeddedChrome && (
                      <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 flex justify-end items-center gap-3">
                        <div className="flex gap-3">
                          {createStep === 1 && (
                            <button
                              type="button"
                              disabled={!draftName.trim()}
                              onClick={() => setCreateStep(2)}
                              data-testid="next-step-btn"
                              className="px-5 py-2.5 rounded-xl bg-[#007BFF] text-sm font-semibold text-white border-none cursor-pointer transition-all duration-200 hover:bg-[#0062CC] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                            >
                              Next: Choose screening methods
                            </button>
                          )}
                          {createStep === 2 && (
                            <button
                              type="button"
                              disabled={
                                !draftName.trim() ||
                                (!includeResumeScoring && !includeAssessment && !includeInterview && !includeDocumentCollection) ||
                                resumeCriteriaCreating
                              }
                              onClick={handleStep2Next}
                              data-testid="next-step-btn"
                              className="px-5 py-2.5 rounded-xl bg-[#007BFF] text-sm font-semibold text-white border-none cursor-pointer transition-all duration-200 hover:bg-[#0062CC] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                            >
                              {includeResumeScoring ? (
                                resumeCriteriaCreating ? (
                                  <span className="inline-flex items-center gap-2">
                                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                    Next: Configure resume scoring
                                  </span>
                                ) : (
                                  'Next: Configure resume scoring'
                                )
                              ) : includeDocumentCollection ? (
                                'Next: Configure BGV documents'
                              ) : includeAssessment ? (
                                'Next: Choose assessment source'
                              ) : includeInterview ? (
                                'Next: Choose interview source'
                              ) : (
                                'Next: Configure selected methods'
                              )}
                            </button>
                          )}
                          {createStep === 3 && includeResumeScoring && (
                            <button
                              type="button"
                              onClick={handleResumeCriteriaNext}
                              data-testid="next-step-btn"
                              className="px-5 py-2.5 rounded-xl bg-[#007BFF] text-sm font-semibold text-white border-none cursor-pointer transition-all duration-200 hover:bg-[#0062CC] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                            >
                              {includeDocumentCollection
                                ? 'Next: Configure BGV documents'
                                : includeAssessment
                                  ? 'Next: Choose assessment source'
                                  : includeInterview
                                    ? 'Next: Choose interview source'
                                    : 'Next: Review'}
                            </button>
                          )}
                          {createStep === 3 + rsOff && includeDocumentCollection && (
                            <button
                              type="button"
                              onClick={handleDocumentCollectionNext}
                              data-testid="next-step-btn"
                              className="px-5 py-2.5 rounded-xl bg-[#007BFF] text-sm font-semibold text-white border-none cursor-pointer transition-all duration-200 hover:bg-[#0062CC] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                            >
                              {includeAssessment
                                ? 'Next: Choose assessment source'
                                : includeInterview
                                  ? 'Next: Choose interview source'
                                  : 'Next: Review'}
                            </button>
                          )}
                          {createStep === 3 + rsOff + dcOff && includeAssessment && !showAssessmentModal && (
                            <button
                              type="button"
                              disabled={!assessmentSource}
                              onClick={handleStep3Next}
                              data-testid="next-step-btn"
                              className="px-5 py-2.5 rounded-xl bg-[#007BFF] text-sm font-semibold text-white border-none cursor-pointer transition-all duration-200 hover:bg-[#0062CC] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                            >
                              {assessmentSource === 'library' ? 'Next: Select from library' : 'Next: Create assessment'}
                            </button>
                          )}
                          {createStep === 3 + rsOff + dcOff && includeInterview && !includeAssessment && !showInterviewModal && !showInterviewLibraryPicker && (
                            <button
                              type="button"
                              disabled={!interviewSource}
                              onClick={handleInterviewSourceNext}
                              data-testid="next-step-btn"
                              className="px-5 py-2.5 rounded-xl bg-[#007BFF] text-sm font-semibold text-white border-none cursor-pointer transition-all duration-200 hover:bg-[#0062CC] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                            >
                              {interviewSource === 'library' ? 'Next: Select from library' : 'Next: Create interview'}
                            </button>
                          )}
                          {createStep === 4 + rsOff + dcOff && includeAssessment && includeInterview && !showInterviewModal && !showInterviewLibraryPicker && (
                            <>
                              {includeAssessment && assessmentConfigured && !interviewConfigured ? (
                                <button
                                  type="button"
                                  disabled={!interviewSource}
                                  onClick={handleInterviewSourceNext}
                                  data-testid="next-step-btn"
                              className="px-5 py-2.5 rounded-xl bg-[#007BFF] text-sm font-semibold text-white border-none cursor-pointer transition-all duration-200 hover:bg-[#0062CC] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                                >
                                  {interviewSource === 'library' ? 'Next: Select from library' : 'Next: Create interview'}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  disabled={
                                    !draftName.trim() ||
                                    (includeAssessment && !assessmentConfigured) ||
                                    (includeInterview && !interviewConfigured)
                                  }
                                  onClick={handleStep4Next}
                                  data-testid="next-step-btn"
                              className="px-5 py-2.5 rounded-xl bg-[#007BFF] text-sm font-semibold text-white border-none cursor-pointer transition-all duration-200 hover:bg-[#0062CC] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                                >
                                  Next: Review
                                </button>
                              )}
                            </>
                          )}
                          {createStep === reviewStep && (
                            <button
                              type="button"
                              disabled={
                                !draftName.trim() ||
                                creating ||
                                (includeAssessment && !assessmentConfigured) ||
                                (includeInterview && !interviewConfigured)
                              }
                              onClick={handleCreateScreening}
                              data-testid="finish-btn"
                              className="px-5 py-2.5 rounded-xl bg-[#007BFF] text-sm font-semibold text-white border-none cursor-pointer transition-all duration-200 hover:bg-[#0062CC] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 shadow-sm hover:shadow-md"
                            >
                              {creating ? (
                                <>
                                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                  <span>Creating...</span>
                                </>
                              ) : (
                                <span>Finish</span>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Discard screening setup confirmation modal */}
          {showDiscardConfirm && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 transition-all duration-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Discard screening setup?</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Click Yes to discard this screening configuration. Any assessment and interview created
                  for this screening will be deleted and this wizard will be closed.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDiscardConfirm(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await handleDiscardConfiguredSteps();
                      } catch {
                        return;
                      }
                      setShowDiscardConfirm(false);
                      setIsCreateOpen(false);
                      setCreateStep(1);
                      setDraftName('');
                      setDraftDescription('');
                      setIncludeResumeScoring(true);
                      setResumeScoringCriteria([]);
                      setIncludeAssessment(false);
                      setIncludeInterview(false);
                      setIncludeDocumentCollection(false);
                      setRequestedDocuments([]);
                      setIncludeApplicationForm(false);
                      setAssessmentSource(null);
                      setConfiguredAssessmentId(null);
                      setConfiguredAssessmentDuration(null);
                      setConfiguredInterviewId(null);
                      setAssessmentConfigured(false);
                      setInterviewConfigured(false);
                      setShowAssessmentModal(false);
                      setShowInterviewModal(false);
                      setShowInterviewLibraryPicker(false);
                    }}
                    className="px-4 py-2.5 text-sm font-medium text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] transition-all duration-200"
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Remove method confirmation modal (when user unchecks after configuring) */}
          {methodRemoveConfirm && (
            <div className="fixed inset-0 z-[101] flex items-center justify-center bg-black/50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 transition-all duration-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{methodRemoveConfirm.title}</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  {methodRemoveConfirm.message}
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setMethodRemoveConfirm(null)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await methodRemoveConfirm.onConfirm?.();
                      } catch {
                        return;
                      } finally {
                        setMethodRemoveConfirm(null);
                      }
                    }}
                    className="px-4 py-2.5 text-sm font-medium text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] transition-all duration-200"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * One screening type in the "Create screening" menu.
 *
 * Each row carries a one-line description, because "Qualifying Agent" and "WhatsApp Agent" do
 * not say what they are to anyone who has not used them — which is exactly why they read as
 * noise when they sat in the header as bare buttons.
 */
const CreateMenuItem = ({ Icon, iconClass = 'text-gray-400', title, desc, onClick, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
  >
    <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${iconClass}`} />
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-gray-900">{title}</span>
      <span className="mt-0.5 block text-xs leading-snug text-gray-500">{desc}</span>
    </span>
  </button>
);

export default JobScreeningsPage;
